import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { keepReadonly } from '../utils/embedParams';
import dagre from '@dagrejs/dagre';
import { Settings2, Globe2, Plus, ChevronDown, ChevronUp, Layout, Users, MapPin, Zap, X, ChevronsDownUp, ChevronsUpDown, Search, EyeOff} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEmbedTheme } from '../hooks/useEmbedTheme';
import {
  ReactFlow,
  ReactFlowInstance,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  NodeTypes,
  EdgeTypes,
  Handle,
  Position,
  NodeProps,
  EdgeProps,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getBezierPath,
  getStraightPath,
  BaseEdge,
  Panel,
  Connection,
  ConnectionMode,
  addEdge,
  reconnectEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { apiGet, apiPost, apiPut, apiDel } from '../api';
import { VocabData, EMPTY_VOCAB } from '../constants/vocab';
import { bareId, entityKind, collectionPath, entityPath } from '../entityIds';
import { summarizeHidden, describeHidden, createdWhileHiddenMessage, typeLabel } from '../world/hiddenTypes';
import { saveForMissingRecord } from '../world/missingRecord';
import { serializeByKey } from '../utils/serializeByKey';
import { parseApiError } from '../apiError';
import '../App.css';
import EntitySidePanel, { PanelEntity, EntityUpdate } from '../components/EntitySidePanel';
import RelationshipPanel, { PanelRelationship, RelationshipUpdate } from '../components/RelationshipPanel';
import ReadOnlyInfoPanel, { ReadOnlyEntityInfo, ReadOnlyRelationshipInfo } from '../components/ReadOnlyInfoPanel';
import VocabularyDrawerBody from '../components/VocabularyDrawerBody';
import { entitiesRequest, filterKey, readIncludeSeries, writeIncludeSeries } from '../world/seriesScope';
import IncludeSeriesSwitch from '../world/IncludeSeriesSwitch';
import { mergeLayout, canSaveLayout, pruneLayout, viewHoldsWholeLayout } from '../world/layoutSave';

// ── Relationship type colors (keyed by REL_TYPE_OPTIONS value) ────────────────

export const REL_TYPE_COLORS: Record<string, string> = {
  related:           '#196773', // chaptery teal — neutral default
  ally:              '#5CA383', // forest green — positive bond
  enemy:             '#C0392B', // strong red — hostile
  rival:             '#E87654', // amber/coral — competitive tension
  mentor:            '#0d5c6e', // deep teal — authority/wisdom
  student:           '#48AAAD', // light teal — learning
  family:            '#7B5EA7', // warm purple — deep bond
  romantic:          '#D4678A', // rose pink — love
  complicated:       '#8E8E93', // muted grey — ambiguity
  participant:       '#c97a1e', // event amber — activity link
  'takes place at':  '#4a9e7c', // earthy green — location link
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Character {
  entityId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
  bookIds?: string[];
  seriesIds?: string[];
}

interface Location {
  entityId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
  bookIds?: string[];
  seriesIds?: string[];
}

interface Event {
  entityId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
  bookIds?: string[];
  seriesIds?: string[];
}

interface Relationship {
  entityId: string;
  sourceId: string;
  targetId: string;
  sourceHandle?: string;
  targetHandle?: string;
  relationshipType: string;
  description?: string;
  metadata?: Record<string, unknown>;
  directed?: boolean;
}

/**
 * The relationship a refused create-only POST /relationships found in the way, or null for
 * any other failure. The page's copy of the relationships can be out of date (another tab,
 * two quick drags), so the server refuses a link that is already there, 409
 * RELATIONSHIP_EXISTS, and sends that one back to be opened. (#973)
 */
export async function relationshipInTheWay(e: unknown): Promise<Relationship | null> {
  const parsed = await parseApiError(e);
  if (parsed.code !== 'RELATIONSHIP_EXISTS') return null;
  const rel = parsed.body?.relationship as Relationship | undefined;
  return rel && typeof rel.entityId === 'string' && rel.sourceId && rel.targetId ? rel : null;
}

/**
 * The body of a relationship panel save.
 *
 * POST /relationships writes the whole row, so every field goes, the ones the edit did
 * not touch filled from the stored copy. That includes the handles: without them the
 * POST stores null and the link forgets which side of each card it attaches to. (#963)
 */
export function relationshipSavePayload(rel: Relationship, updates: RelationshipUpdate) {
  return {
    sourceId: rel.sourceId,
    targetId: rel.targetId,
    sourceHandle: rel.sourceHandle ?? null,
    targetHandle: rel.targetHandle ?? null,
    relationshipType: updates.relationshipType ?? rel.relationshipType,
    description: updates.description ?? rel.description ?? '',
    metadata: updates.metadata !== undefined
      ? { ...(rel.metadata ?? {}), ...updates.metadata }
      : rel.metadata ?? {},
    directed: updates.directed ?? rel.directed ?? false,
  };
}

interface BookOption { entityId: string; title: string; }
interface SeriesOption { entityId: string; title: string; books?: { bookId: string; sequence?: number }[]; }

interface PanelRelationshipState {
  rel: PanelRelationship;
  source: PanelEntity;
  target: PanelEntity;
}

// ── Focus helper ──────────────────────────────────────────────────────────────

// Returns the selected node + its direct (depth-1) neighbours only.
// Transitive BFS would light up too much of a well-connected graph.
function getConnectedIds(nodeId: string, edges: Edge[]): Set<string> {
  const connected = new Set<string>([nodeId]);
  for (const edge of edges) {
    if (edge.source === nodeId) connected.add(edge.target);
    if (edge.target === nodeId) connected.add(edge.source);
  }
  return connected;
}

// ── Custom nodes ──────────────────────────────────────────────────────────────

const NodeHandles: React.FC<{ id: string }> = ({ id }) => (
  <>
    <Handle type="source" position={Position.Top}    id={`${id}-t`} />
    <Handle type="source" position={Position.Right}  id={`${id}-r`} />
    <Handle type="source" position={Position.Bottom} id={`${id}-b`} />
    <Handle type="source" position={Position.Left}   id={`${id}-l`} />
  </>
);

interface CardNodeProps {
  id: string;
  data: Record<string, unknown>;
  typeLabel: string;
  accentClass: string;
  defaultIcon: string;
}

const CardNode: React.FC<CardNodeProps> = ({ id, data, typeLabel, accentClass, defaultIcon }) => {
  const imageUrl = data.imageUrl as string | undefined;
  const description = data.description as string | undefined;
  const label = data.label as string;
  const scopeTags = (data.scopeTags as ScopeTag[]) ?? [];
  const collapsed = (data.collapsed as boolean | undefined) ?? false;
  const onToggleCollapse = data.onToggleCollapse as ((id: string) => void) | undefined;

  const toggleBtn = (
    <button
      className="rf-card-collapse-btn nodrag"
      title={collapsed ? 'Expand' : 'Collapse'}
      onClick={e => { e.stopPropagation(); onToggleCollapse?.(id); }}
    >
      {collapsed ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
    </button>
  );

  if (collapsed) {
    return (
      <div className={`rf-card-node ${accentClass} collapsed`}>
        <NodeHandles id={id} />
        <span className="rf-card-collapsed-name">{label}</span>
        <span className="rf-card-collapsed-type">{typeLabel}</span>
        {toggleBtn}
      </div>
    );
  }

  return (
    <div className={`rf-card-node ${accentClass}`}>
      <NodeHandles id={id} />
      {toggleBtn}

      <div className="rf-card-top">
        <div className="rf-card-thumb">
          {imageUrl
            ? <img className="rf-card-thumb-img" src={imageUrl} alt={label} />
            : <span className="rf-card-thumb-icon">{defaultIcon}</span>
          }
        </div>
        <div className="rf-card-header">
          <div className="rf-card-name">{label}</div>
          <div className="rf-card-type">{typeLabel}</div>
        </div>
      </div>

      {description && description.trim() && (
        <p className="rf-card-desc">{description}</p>
      )}

      {scopeTags.length > 0 && (
        <div className="rf-card-tags">
          {scopeTags.map(tag => (
            <span
              key={`${tag.kind}-${tag.title}`}
              className={`rf-card-tag rf-card-tag--${tag.kind}`}
              title={tag.kind === 'series' ? 'Series association' : 'Book association'}
            >
              {tag.title}
            </span>
          ))}
        </div>
      )}

    </div>
  );
};

const CharacterNode: React.FC<NodeProps> = ({ id, data }) => (
  <CardNode id={id} data={data as Record<string, unknown>} typeLabel="CHARACTER" accentClass="rf-card-character" defaultIcon="👤" />
);

const LocationNode: React.FC<NodeProps> = ({ id, data }) => (
  <CardNode id={id} data={data as Record<string, unknown>} typeLabel="LOCATION" accentClass="rf-card-location" defaultIcon="📍" />
);

const EventNode: React.FC<NodeProps> = ({ id, data }) => (
  <CardNode id={id} data={data as Record<string, unknown>} typeLabel="EVENT" accentClass="rf-card-event" defaultIcon="⚡" />
);

/**
 * Which entities request backs a given World Building scope.
 *
 * Exported so the path is pinned by a test. Nothing asserted this before, which is
 * why a request to a non-existent route survived six months. (#751)
 */
export function scopedEntitiesPath(scope: string | null, id: string | null, seriesIds: string[] = []): string {
  if (scope === 'book' && id) {
    const book = `/entities?bookId=${encodeURIComponent(`BOOK#${id}`)}`;
    // The series this book belongs to, when "Include entries from the series" is on (#1138).
    return seriesIds.length ? `${book}&seriesId=${encodeURIComponent(seriesIds.join(','))}` : book;
  }
  if (scope === 'series' && id) return `/entities?seriesId=${encodeURIComponent(`SERIES#${id}`)}`;
  return '/entities';
}

/**
 * The relationship already stored from sourceId to targetId, other than exceptId.
 *
 * A relationship's key is made from its source and target, and POST /relationships
 * writes the whole row, so writing one for a pair that already has one overwrites it:
 * type, description, status and direction all reset. Connect, reconnect and swap ask
 * this first, and open the relationship that is there instead. (#966)
 */
export function relationshipBetween<R extends { entityId: string; sourceId: string; targetId: string }>(
  rels: R[], sourceId: string, targetId: string, exceptId?: string,
): R | undefined {
  return rels.find(r => r.sourceId === sourceId && r.targetId === targetId && r.entityId !== exceptId);
}

/**
 * What the canvas asks before destroying entities.
 *
 * Deleting a node takes its relationships with it, so the count has to be in the
 * question: losing relationships you authored is the part nobody expects and the part
 * that cannot be undone. The edge list is ReactFlow's own — the exact set it is about
 * to remove, which includes separately selected edges and not just the connected ones —
 * so the prompt cannot undercount what is about to go. (#785)
 */
export function deleteConfirmMessage(
  toDelete: { id: string; data?: unknown }[],
  doomedEdges: { source: string; target: string }[],
): string {
  const names = toDelete.map(n => (n.data as { label?: string })?.label?.trim() || 'this entity');
  const subject = names.length === 1 ? `"${names[0]}"` : `${names.length} entities`;

  const ids = new Set(toDelete.map(n => n.id));
  const related = doomedEdges.filter(e => ids.has(e.source) || ids.has(e.target)).length;
  const also = related === 0 ? ''
    : related === 1 ? '\n\n1 relationship will be deleted too.'
    : `\n\n${related} relationships will be deleted too.`;

  return `Delete ${subject}? This cannot be undone.${also}`;
}

/**
 * Delete entities server-side, returning the ids that failed.
 *
 * Kept out of the component, with its deleter injected, so the destructive path has an
 * executable test rather than a grep. One failure must not abandon the rest: with a
 * multi-select the user asked for all of them to go. (#785)
 */
export async function deleteEntities(
  ids: string[],
  del: (path: string) => Promise<unknown>,
): Promise<string[]> {
  const failed: string[] = [];
  for (const id of ids) {
    try { await del(entityPath(id)); }
    catch (e) { console.error('Failed to delete entity', id, e); failed.push(id); }
  }
  return failed;
}

/**
 * The books and series a panel save takes an entity out of.
 *
 * POST /<entity> adds the bookIds and seriesIds it is sent to the stored ones and never
 * drops any, so unticking a box has to be said outright: the panel names the one box
 * unticked, and the save passes it on as removeBookIds / removeSeriesIds. Nothing is
 * worked out by comparing the panel's ticks with the stored copy: either may be stale
 * (a filter change reloads one and not the other), and a book added elsewhere would be
 * taken away. (#964)
 */
export function scopeRemovals(updates: EntityUpdate): { removeBookIds?: string[]; removeSeriesIds?: string[] } {
  return {
    ...(updates.removeBookIds?.length ? { removeBookIds: updates.removeBookIds } : {}),
    ...(updates.removeSeriesIds?.length ? { removeSeriesIds: updates.removeSeriesIds } : {}),
  };
}

const nodeTypes: NodeTypes = { character: CharacterNode, location: LocationNode, event: EventNode };

// ── Custom edge ───────────────────────────────────────────────────────────────

type EdgeRouting = 'smoothstep' | 'step' | 'bezier' | 'straight';

const RelationshipEdge: React.FC<EdgeProps> = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
  data, style,
}) => {
  const onEdit = data?.onEdit as ((id: string) => void) | undefined;
  const label = data?.label as string | undefined;
  const routing = (data?.routing as EdgeRouting) ?? 'smoothstep';
  const directed = (data?.directed as boolean) ?? false;
  const labelOffset = data?.labelOffset as { dx: number; dy: number } | undefined;

  const pathArgs = { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition };
  const [edgePath, labelX, labelY] =
    routing === 'bezier'   ? getBezierPath(pathArgs) :
    routing === 'straight' ? getStraightPath(pathArgs) :
    routing === 'step'     ? getSmoothStepPath({ ...pathArgs, borderRadius: 0 }) :
                             getSmoothStepPath(pathArgs);

  const typeColor = REL_TYPE_COLORS[label ?? ''] ?? '#196773';
  // EdgeLabelRenderer renders in HTML, not SVG — opacity from the edge style won't
  // cascade to the pill automatically, so we read and re-apply it explicitly.
  const edgeOpacity = (style as React.CSSProperties | undefined)?.opacity as number | undefined;
  const markerId = directed ? `arrow-${id.replace(/[^a-zA-Z0-9]/g, '_')}` : undefined;

  return (
    <>
      {directed && markerId && (
        <defs>
          <marker
            id={markerId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 1 L 9 5 L 0 9 Z" fill={typeColor} />
          </marker>
        </defs>
      )}
      <BaseEdge
        path={edgePath}
        style={{ ...style, stroke: typeColor }}
        interactionWidth={12}
        markerEnd={directed && markerId ? `url(#${markerId})` : undefined}
      />
      <EdgeLabelRenderer>
        <div
          className="rf-edge-label-pill nopan nodrag"
          style={{
            transform: `translate(-50%,-50%) translate(${labelX + (labelOffset?.dx ?? 0)}px,${labelY + (labelOffset?.dy ?? 0)}px)`,
            borderLeft: `4px solid ${typeColor}`,
            opacity: edgeOpacity ?? 1,
          }}
          onClick={e => { e.stopPropagation(); onEdit?.(id); }}
          onDoubleClick={e => { e.stopPropagation(); onEdit?.(id); }}
          title="Click to edit"
        >
          {label}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes: EdgeTypes = { relationship: RelationshipEdge };

// Spread edge labels so they don't stack. Each label renders at its edge's
// midpoint, so parallel edges — and edges converging on a hub — pile their labels
// on the same spot. Treat each label as a small box, relax overlaps apart over a
// few passes, and ease each back toward its own edge so none drift far. Returns a
// per-edge { dx, dy } offset (flow units) applied by RelationshipEdge.
function resolveLabelOffsets(
  centers: Map<string, { x: number; y: number }>,
  edges: Array<{ id: string; source: string; target: string; data?: Record<string, unknown> }>,
): Map<string, { dx: number; dy: number }> {
  type L = { id: string; ax: number; ay: number; x: number; y: number; w: number; h: number };
  const items: L[] = [];
  edges.forEach(e => {
    const label = e.data?.label as string | undefined;
    if (!label) return;
    const s = centers.get(e.source);
    const t = centers.get(e.target);
    if (!s || !t) return;
    const ax = (s.x + t.x) / 2, ay = (s.y + t.y) / 2;
    items.push({ id: e.id, ax, ay, x: ax, y: ay, w: Math.min(180, label.length * 6.4 + 22), h: 26 });
  });
  const PAD = 6;
  for (let iter = 0; iter < 120; iter++) {
    let moved = false;
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i], b = items[j];
        const ox = (a.w + b.w) / 2 + PAD - Math.abs(a.x - b.x);
        const oy = (a.h + b.h) / 2 + PAD - Math.abs(a.y - b.y);
        if (ox > 0 && oy > 0) {
          moved = true;
          if (ox <= oy) { const p = ox / 2, dir = a.x <= b.x ? 1 : -1; a.x -= dir * p; b.x += dir * p; }
          else          { const p = oy / 2, dir = a.y <= b.y ? 1 : -1; a.y -= dir * p; b.y += dir * p; }
        }
      }
    }
    for (const it of items) { it.x += (it.ax - it.x) * 0.05; it.y += (it.ay - it.y) * 0.05; }
    if (!moved) break;
  }
  const out = new Map<string, { dx: number; dy: number }>();
  for (const it of items) {
    out.set(it.id, {
      dx: Math.max(-70, Math.min(70, it.x - it.ax)),
      dy: Math.max(-70, Math.min(70, it.y - it.ay)),
    });
  }
  return out;
}

// ── Layout helpers ────────────────────────────────────────────────────────────

interface ScopeTag { title: string; kind: 'book' | 'series'; }

function buildNodes(
  characters: Character[],
  locations: Location[],
  events: Event[],
  savedPositions: Record<string, { x: number; y: number }>,
  onEditNode: (id: string) => void,
  onToggleCollapse: (id: string) => void,
  bookTitleMap: Map<string, string> = new Map(),
  seriesTitleMap: Map<string, string> = new Map(),
): Node[] {
  const total = characters.length + locations.length + events.length;
  const cols = Math.max(3, Math.ceil(Math.sqrt(total)));
  const W = 260, H = 160;
  const nodes: Node[] = [];

  const resolveTags = (bookIds: string[], seriesIds: string[]): ScopeTag[] => {
    const tags: ScopeTag[] = [];
    for (const id of bookIds ?? []) { const t = bookTitleMap.get(id); if (t) tags.push({ title: t, kind: 'book' }); }
    for (const id of seriesIds ?? []) { const t = seriesTitleMap.get(id); if (t) tags.push({ title: t, kind: 'series' }); }
    return tags;
  };

  characters.forEach((c, i) => {
    const pos = savedPositions[c.entityId] ?? { x: (i % cols) * W + 40, y: Math.floor(i / cols) * H + 40 };
    const scopeTags = resolveTags(c.bookIds ?? [], c.seriesIds ?? []);
    nodes.push({
      id: c.entityId, type: 'character', position: pos,
      data: {
        label: c.name,
        entityType: 'character',
        description: c.description ?? '',
        imageUrl: c.imageUrl ?? '',
        bookIds: c.bookIds ?? [],
        seriesIds: c.seriesIds ?? [],
        scopeTags,
        collapsed: false,
        onEditNode,
        onToggleCollapse,
      },
    });
  });

  const charRows = Math.ceil(characters.length / cols);
  locations.forEach((l, i) => {
    const pos = savedPositions[l.entityId] ?? {
      x: (i % cols) * W + 40,
      y: charRows * H + Math.floor(i / cols) * H + 80,
    };
    const scopeTags = resolveTags(l.bookIds ?? [], l.seriesIds ?? []);
    nodes.push({
      id: l.entityId, type: 'location', position: pos,
      data: {
        label: l.name,
        entityType: 'location',
        description: l.description ?? '',
        imageUrl: l.imageUrl ?? '',
        bookIds: l.bookIds ?? [],
        seriesIds: l.seriesIds ?? [],
        scopeTags,
        collapsed: false,
        onEditNode,
        onToggleCollapse,
      },
    });
  });

  const locRows = Math.ceil(locations.length / cols);
  events.forEach((ev, i) => {
    const pos = savedPositions[ev.entityId] ?? {
      x: (i % cols) * W + 40,
      y: (charRows + locRows) * H + Math.floor(i / cols) * H + 120,
    };
    const scopeTags = resolveTags(ev.bookIds ?? [], ev.seriesIds ?? []);
    nodes.push({
      id: ev.entityId, type: 'event', position: pos,
      data: {
        label: ev.name,
        entityType: 'event',
        description: ev.description ?? '',
        imageUrl: ev.imageUrl ?? '',
        bookIds: ev.bookIds ?? [],
        seriesIds: ev.seriesIds ?? [],
        scopeTags,
        collapsed: false,
        onEditNode,
        onToggleCollapse,
      },
    });
  });

  return nodes;
}

function bestHandles(
  sourceId: string, targetId: string,
  positions: Record<string, { x: number; y: number }>,
): { sourceHandle: string; targetHandle: string } | null {
  const sp = positions[sourceId];
  const tp = positions[targetId];
  if (!sp || !tp) return null;
  const dx = tp.x - sp.x;
  const dy = tp.y - sp.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { sourceHandle: `${sourceId}-r`, targetHandle: `${targetId}-l` }
      : { sourceHandle: `${sourceId}-l`, targetHandle: `${targetId}-r` };
  }
  return dy >= 0
    ? { sourceHandle: `${sourceId}-b`, targetHandle: `${targetId}-t` }
    : { sourceHandle: `${sourceId}-t`, targetHandle: `${targetId}-b` };
}

function buildEdges(
  relationships: Relationship[],
  onEdit: (id: string) => void,
  onUpdateWaypoint: (id: string, x: number, y: number) => void,
  waypoints: Record<string, { x: number; y: number }> = {},
  routing: EdgeRouting = 'smoothstep',
  savedPositions: Record<string, { x: number; y: number }> = {},
): Edge[] {
  return relationships.map(r => {
    const stored = r.sourceHandle && r.targetHandle
      ? { sourceHandle: r.sourceHandle, targetHandle: r.targetHandle }
      : bestHandles(r.sourceId, r.targetId, savedPositions);
    return {
      id: r.entityId,
      source: r.sourceId,
      target: r.targetId,
      sourceHandle: stored?.sourceHandle ?? null,
      targetHandle: stored?.targetHandle ?? null,
      type: 'relationship',
      style: { stroke: '#196773', strokeWidth: 1.5 },
      data: {
        label: r.relationshipType,
        description: r.description,
        onEdit,
        onUpdateWaypoint,
        waypoint: waypoints[r.entityId] ?? null,
        routing,
        directed: r.directed ?? false,
      },
    };
  });
}

// ── Layout settings ──────────────────────────────────────────────────────────

type LayoutSettings = {
  rankdir: 'LR' | 'TB';
  spacing: 'compact' | 'normal' | 'spacious';
  edgeRouting: EdgeRouting;
};

const DIRECTION_LABELS: Record<LayoutSettings['rankdir'], string> = {
  LR: '→ LR', TB: '↓ TB',
};
const SPACING_LABELS: Record<LayoutSettings['spacing'], string> = {
  compact: 'Compact', normal: 'Normal', spacious: 'Spacious',
};
const EDGE_ROUTING_LABELS: Record<EdgeRouting, string> = {
  smoothstep: 'Curved Steps', bezier: 'Smooth Curves', step: 'Sharp Steps', straight: 'Straight',
};

const SPACING_MAP: Record<LayoutSettings['spacing'], { nodesep: number; ranksep: number }> = {
  compact:  { nodesep: 30,  ranksep: 50  },
  normal:   { nodesep: 60,  ranksep: 80  },
  spacious: { nodesep: 100, ranksep: 140 },
};

const DEFAULT_LAYOUT: LayoutSettings = { rankdir: 'LR', spacing: 'normal', edgeRouting: 'smoothstep' };

function loadLayoutSettings(): LayoutSettings {
  try {
    const saved = JSON.parse(localStorage.getItem('rf-layout-settings') ?? '{}') as Partial<LayoutSettings>;
    return { ...DEFAULT_LAYOUT, ...saved };
  } catch { return DEFAULT_LAYOUT; }
}

type EntityKind = 'character' | 'location' | 'event';
type TypeVisibility = Record<EntityKind, boolean>;
const ALL_TYPES_VISIBLE: TypeVisibility = { character: true, location: true, event: true };
const TYPE_META: { key: EntityKind; label: string; color: string }[] = [
  { key: 'character', label: 'Characters', color: '#196773' },
  { key: 'location', label: 'Locations', color: '#5CA383' },
  { key: 'event', label: 'Events', color: '#c97a1e' },
];

function loadTypeVisibility(): TypeVisibility {
  try {
    const saved = JSON.parse(localStorage.getItem('rf-type-visibility') ?? '{}') as Partial<TypeVisibility>;
    return { ...ALL_TYPES_VISIBLE, ...saved };
  } catch { return ALL_TYPES_VISIBLE; }
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface RelationshipPageProps {}

const RelationshipPage: React.FC<RelationshipPageProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Desktop client sends ?bookId=BOOK#<uuid> — strip prefix to get bare UUID
  const bookIdFlag = searchParams.get('bookId')?.replace('BOOK#', '') || '';
  const scopeParam = (searchParams.get('scope') as 'book' | 'series' | null) ?? (bookIdFlag ? 'book' : null);
  const idParam = searchParams.get('id') || bookIdFlag;
  const isReadonly = searchParams.get('readonly') === 'true';
  const embedTheme = useEmbedTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedParam = searchParams.get('selected') ?? '';

  const [books, setBooks] = useState<BookOption[]>([]);
  const [seriesList, setSeriesList] = useState<SeriesOption[]>([]);
  // #1138: a book's view can include the series it belongs to. On unless turned off.
  const [includeSeries, setIncludeSeries] = useState<boolean>(readIncludeSeries);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  // False until GET /series is read: a failed read must not pass for a book in no series.
  const [seriesKnown, setSeriesKnown] = useState(false);
  // Which series to ask for, and whether to show the switch (tested in world/seriesScope).
  // Memoised so the reload effect sees a new array only when the switch or the book's series change.
  const { bookSeries, seriesIds: includedSeriesIds, showSwitch } = useMemo(
    () => entitiesRequest(scopeParam, idParam, includeSeries, seriesList),
    [scopeParam, idParam, includeSeries, seriesList],
  );
  const includedSeriesKey = includedSeriesIds.join(',');
  // Whether the load returns every entry its layout can hold, so positions of anything else can
  // be pruned. A book view whose series could not be read may be partial, so it keeps them all.
  const wholeLayout = viewHoldsWholeLayout(scopeParam, seriesKnown, includedSeriesIds.length, bookSeries.length);
  const handleIncludeSeries = (on: boolean) => { setIncludeSeries(on); writeIncludeSeries(on); };
  // A book view needs the series list to know which series to ask for, so it waits for it.
  const awaitingSeries = scopeParam === 'book' && !filtersLoaded;
  const scopeName = useMemo(() => {
    if (!scopeParam || !idParam) return 'All Entities';
    if (scopeParam === 'book') {
      const bookId = idParam.startsWith('BOOK#') ? idParam : `BOOK#${idParam}`;
      return books.find(b => b.entityId === bookId)?.title ?? idParam;
    }
    const seriesId = idParam.startsWith('SERIES#') ? idParam : `SERIES#${idParam}`;
    return seriesList.find(s => s.entityId === seriesId)?.title ?? idParam;
  }, [scopeParam, idParam, books, seriesList]);
  const [loading, setLoading] = useState(true);
  // Dismissing the restore bar is per-visit, never persisted. Persisting it would
  // recreate the silent-filter trap one level up. (#754)
  const [restoreBarDismissed, setRestoreBarDismissed] = useState(false);

  // Set when someone creates an entity of a type they have hidden: the one moment a
  // silent filter is most likely to read as "creating is broken". (#754)
  const [createdHidden, setCreatedHidden] = useState<{ message: string; kind: EntityKind } | null>(null);
  const [error, setError] = useState('');
  // A failed layout read leaves card moves unsaved (#1138), so the page says so and can retry.
  const [layoutReadFailed, setLayoutReadFailed] = useState(false);
  const [layoutRetry, setLayoutRetry] = useState(0);

  // Entity arrays (populated in loadEntities, read by panel handlers)
  const charactersRef = useRef<Character[]>([]);
  const locationsRef = useRef<Location[]>([]);
  const eventsRef = useRef<Event[]>([]);

  // Side panel state — mutually exclusive
  const [selectedPanelEntity, setSelectedPanelEntity] = useState<PanelEntity | null>(null);
  const [selectedPanelRelationship, setSelectedPanelRelationship] = useState<PanelRelationshipState | null>(null);

  // Focus/highlight mode
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  // Per-type visibility (hide all nodes of a type + their relationships)
  const [typeVisibility, setTypeVisibility] = useState<TypeVisibility>(loadTypeVisibility);
  useEffect(() => {
    localStorage.setItem('rf-type-visibility', JSON.stringify(typeVisibility));
  }, [typeVisibility]);

  // Name search — dims everything that doesn't match (like selecting a node, but
  // for every match). Selecting a node exits search mode; focusing the box re-enters.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Right-click context menu for selecting edges at a node
  const [nodeContextMenu, setNodeContextMenu] = useState<{ nodeId: string; edgeIds: string[]; x: number; y: number } | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // What the View filter is currently removing. Derived rather than stored so it can
  // never disagree with the canvas. (#754)
  const hidden = useMemo(
    () => summarizeHidden(nodes, typeVisibility),
    [nodes, typeVisibility],
  );
  const hiddenPhrase = useMemo(() => describeHidden(hidden), [hidden]);

  // Clear the notice the moment its type is visible again, whether that came from the
  // notice's own button, the drawer, or the restore bar. One dismissal path, not three.
  useEffect(() => {
    if (createdHidden && typeVisibility[createdHidden.kind]) setCreatedHidden(null);
  }, [createdHidden, typeVisibility]);

  const rfInstance = useRef<ReactFlowInstance | null>(null);

  const [vocab, setVocab] = useState<VocabData>(EMPTY_VOCAB);
  const [drawer, setDrawer] = useState<'view' | 'world' | null>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [worldTab, setWorldTab] = useState<'relTypes' | 'roles' | 'archetypes'>('relTypes');

  const splitBtnRef = useRef<HTMLDivElement>(null);

  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>(loadLayoutSettings);
  const layoutSettingsRef = useRef<LayoutSettings>(layoutSettings);
  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (splitBtnRef.current && !splitBtnRef.current.contains(e.target as HTMLElement)) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addMenuOpen]);

  useEffect(() => {
    layoutSettingsRef.current = layoutSettings;
    localStorage.setItem('rf-layout-settings', JSON.stringify(layoutSettings));
  }, [layoutSettings]);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const layoutKey = useRef(idParam || 'global');
  const relationshipsRef = useRef<Relationship[]>([]);
  /* What was deleted on this page and is not held again, so a save that finds nothing can
     tell an edit that reached a record as it went (dropped quietly) from one the page lost
     (an error): saveForMissingRecord. (#974) A relationship's id is its two ends, so one
     drawn again between the same two, here or elsewhere, comes back under its old id: each
     place a relationship is added takes it out again, or a later save that found it gone
     (out of the filter, say) would be dropped as if deleted. */
  const deletedHere = useRef(new Set<string>());
  const waypointsRef = useRef<Record<string, { x: number; y: number }>>({});
  /* Every position in the layout record as last loaded or saved. A book view with the series
     switch off puts only the book's own entries on the canvas, and a layout PUT replaces the
     whole record, so saving the canvas alone would erase the series entries' positions
     (#1138). Every save merges the canvas over this. */
  const loadSeq = useRef(0);
  const savedPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  /* The key whose layout was read in full. Null while one loads, and after a failed read, so a
     save then cannot replace a record the page never saw (#1138). */
  const layoutReadyFor = useRef<string | null>(null);
  // Synchronous, and the key is taken when called, so a save made just before a reload goes to the layout it came from.
  const saveLayout = useCallback((canvasPositions: Record<string, { x: number; y: number }>, key = layoutKey.current) => {
    if (!canSaveLayout(layoutReadyFor.current, key)) return;
    const merged = mergeLayout(savedPositionsRef.current, canvasPositions, deletedHere.current);
    savedPositionsRef.current = merged;
    apiPut(`/layouts/${key}`, { positions: merged, waypoints: waypointsRef.current, settings: layoutSettingsRef.current }).catch(console.error);
  }, []);
  const saveCanvasLayout = useCallback(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    nodesRef.current.forEach(n => { positions[n.id] = n.position; });
    saveLayout(positions);
  }, [saveLayout]);
  /* A drag save waiting on its timer is sent now, before a reload empties the canvas, so the
     last move is kept and the timer cannot fire on an empty canvas. */
  const flushPendingLayoutSave = useCallback(() => {
    if (!saveTimer.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    saveCanvasLayout();
  }, [saveCanvasLayout]);
  const edgesRef = useRef<Edge[]>([]);
  const nodesRef = useRef<Node[]>([]);
  const autoSelectedRef = useRef(false);
  const swappingRef = useRef(false);
  const booksRef = useRef<BookOption[]>([]);
  const seriesListRef = useRef<SeriesOption[]>([]);

  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  // Sync edge routing to all existing edges when settings change
  useEffect(() => {
    setEdges(prev => prev.map(e => ({ ...e, data: { ...(e.data as object), routing: layoutSettings.edgeRouting } })));
  }, [layoutSettings.edgeRouting, setEdges]);

  // ── Load filter options ───────────────────────────────────────────────────

  useEffect(() => {
    Promise.all([apiGet('/books'), apiGet('/series')]).then(async ([bRes, sRes]) => {
      const b = await bRes.body.json() as unknown;
      const s = await sRes.body.json() as unknown;
      const bookList = Array.isArray(b) ? b as BookOption[] : [];
      const seriesList_ = Array.isArray(s) ? s as SeriesOption[] : [];
      booksRef.current = bookList;
      seriesListRef.current = seriesList_;
      setBooks(bookList);
      setSeriesList(seriesList_);
      setSeriesKnown(Array.isArray(s));
      setFiltersLoaded(true);
    }).catch(err => {
      console.error(err);
      setFiltersLoaded(true); // a failed series load still lets the book view load, without its series
    });
  }, []);

  useEffect(() => {
    apiGet('/vocab').then(async res => {
      setVocab(await res.body.json() as unknown as VocabData);
    }).catch(() => { /* leave as EMPTY_VOCAB */ });
  }, []);

  // ── Focus effect ──────────────────────────────────────────────────────────

  // Per-type hiding + dimming (search or focus) resolved in one place so they
  // don't fight over node/edge opacity. `lit` = nodes shown at full opacity;
  // null means everything is lit.
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const searching = searchActive && q.length > 0;
    let lit: Set<string> | null = null;
    if (searching) {
      lit = new Set(
        nodesRef.current
          .filter(n => String(n.data?.label ?? '').toLowerCase().includes(q))
          .map(n => n.id),
      );
    } else if (focusedNodeId) {
      lit = getConnectedIds(focusedNodeId, edgesRef.current);
    }
    const typeOf = new Map(nodesRef.current.map(n => [n.id, n.type as EntityKind]));
    const typeHidden = (t?: EntityKind) => !!t && !typeVisibility[t];

    setNodes(ns => ns.map(n => ({
      ...n,
      hidden: typeHidden(n.type as EntityKind),
      style: { ...n.style, opacity: !lit || lit.has(n.id) ? 1 : 0.15 },
    })));
    setEdges(es => es.map(e => ({
      ...e,
      hidden: typeHidden(typeOf.get(e.source as string)) || typeHidden(typeOf.get(e.target as string)),
      style: {
        ...e.style,
        opacity: !lit || (lit.has(e.source as string) && lit.has(e.target as string)) ? 1 : 0.15,
      },
    })));
  }, [focusedNodeId, searchActive, searchQuery, typeVisibility, nodes.length, setNodes, setEdges]);

  // ── Node click → focus toggle ─────────────────────────────────────────────

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setNodeContextMenu(null);
    const entityType = node.type as 'character' | 'location' | 'event';
    const arr = entityType === 'character' ? charactersRef.current
      : entityType === 'location' ? locationsRef.current
      : eventsRef.current;
    const full = arr.find(e => e.entityId === node.id);
    setSelectedPanelEntity({
      entityId: node.id,
      entityType,
      name: node.data.label as string,
      description: (node.data.description as string) ?? '',
      metadata: full?.metadata ?? {},
      bookIds: (node.data.bookIds as string[]) ?? [],
      seriesIds: (node.data.seriesIds as string[]) ?? [],
    });
    setSelectedPanelRelationship(null);
    setFocusedNodeId(node.id);
    setSearchActive(false); // selecting a node exits search mode
  }, []);

  useEffect(() => {
    if (!selectedParam || nodes.length === 0 || autoSelectedRef.current) return;
    const node = nodes.find(n => n.id === selectedParam);
    if (!node) return;
    autoSelectedRef.current = true;
    handleNodeClick({} as React.MouseEvent, node);
  }, [nodes, selectedParam, handleNodeClick]);

  const handlePaneClick = useCallback(() => {
    setNodeContextMenu(null);
    setSelectedPanelEntity(null);
    setSelectedPanelRelationship(null);
    setFocusedNodeId(null);
  }, []);

  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    const connected = edges.filter(e => e.source === node.id || e.target === node.id);

    // Group by the handle on this node's side
    const byHandle = new Map<string | null, Edge[]>();
    for (const edge of connected) {
      const h = (edge.source === node.id ? edge.sourceHandle : edge.targetHandle) ?? null;
      if (!byHandle.has(h)) byHandle.set(h, []);
      byHandle.get(h)!.push(edge);
    }

    // Only show when at least one handle has 2+ edges stacked on it
    const collidingEdgeIds = [...byHandle.values()]
      .filter(group => group.length > 1)
      .flat()
      .map(e => e.id);

    if (collidingEdgeIds.length === 0) return;

    const APPROX_MENU_H = 48 + collidingEdgeIds.length * 40;
    const y = event.clientY + APPROX_MENU_H > window.innerHeight
      ? event.clientY - APPROX_MENU_H
      : event.clientY;
    setNodeContextMenu({ nodeId: node.id, edgeIds: collidingEdgeIds, x: event.clientX, y });
  }, [edges]);

  const buildOpenUrl = useCallback((entityId: string) => {
    const params = new URLSearchParams();
    if (scopeParam) params.set('scope', scopeParam);
    if (idParam) params.set('id', idParam);
    params.set('selected', entityId);
    return `${window.location.origin}/world?${params.toString()}`;
  }, [scopeParam, idParam]);

  // ── Collapse toggle ───────────────────────────────────────────────────────

  const handleToggleCollapse = useCallback((nodeId: string) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, collapsed: !n.data.collapsed } } : n
    ));
  }, [setNodes]);

  const handleCollapseAll = useCallback(() => {
    setNodes(ns => ns.map(n => ({ ...n, data: { ...n.data, collapsed: true } })));
  }, [setNodes]);

  const handleExpandAll = useCallback(() => {
    setNodes(ns => ns.map(n => ({ ...n, data: { ...n.data, collapsed: false } })));
  }, [setNodes]);

  // ── Open node editor ──────────────────────────────────────────────────────

  const openNodeEditor = useCallback((nodeId: string) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    const entityType = node.type as 'character' | 'location' | 'event';
    const arr = entityType === 'character' ? charactersRef.current
      : entityType === 'location' ? locationsRef.current
      : eventsRef.current;
    const full = arr.find(e => e.entityId === nodeId);
    setSelectedPanelEntity({
      entityId: nodeId,
      entityType,
      name: node.data.label as string,
      description: (node.data.description as string) ?? '',
      imageUrl: (node.data.imageUrl as string) ?? '',
      metadata: full?.metadata ?? {},
      bookIds: (node.data.bookIds as string[]) ?? [],
      seriesIds: (node.data.seriesIds as string[]) ?? [],
    });
    setSelectedPanelRelationship(null);
    setFocusedNodeId(null);
  }, []);

  const openNewEntityPanel = useCallback((entityType: 'character' | 'location' | 'event') => {
    const preBookIds = scopeParam === 'book' && idParam ? [`BOOK#${idParam}`] : [];
    const preSeriesIds = scopeParam === 'series' && idParam ? [`SERIES#${idParam}`] : [];
    setSelectedPanelEntity({
      entityId: '',
      entityType,
      name: '',
      description: '',
      metadata: {},
      bookIds: preBookIds,
      seriesIds: preSeriesIds,
    });
    setSelectedPanelRelationship(null);
  }, [scopeParam, idParam]);

  // ── Waypoint update ───────────────────────────────────────────────────────

  const handleUpdateWaypoint = useCallback((edgeId: string, x: number, y: number) => {
    waypointsRef.current[edgeId] = { x, y };
    setEdges(eds => eds.map(e =>
      e.id === edgeId ? { ...e, data: { ...e.data, waypoint: { x, y } } } : e
    ));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; saveCanvasLayout(); }, 1000);
  }, [setEdges, saveCanvasLayout]);

  // ── Edge panel ────────────────────────────────────────────────────────────

  const openEdgePanel = useCallback((edgeId: string) => {
    const rel = relationshipsRef.current.find(r => r.entityId === edgeId);
    if (!rel) return;
    const sourceNode = nodesRef.current.find(n => n.id === rel.sourceId);
    const targetNode = nodesRef.current.find(n => n.id === rel.targetId);
    if (!sourceNode || !targetNode) return;

    const toPanel = (node: typeof sourceNode): PanelEntity => {
      const arr = node!.type === 'character' ? charactersRef.current
        : node!.type === 'location' ? locationsRef.current
        : eventsRef.current;
      const full = arr.find(e => e.entityId === node!.id);
      return {
        entityId: node!.id,
        entityType: node!.type as 'character' | 'location' | 'event',
        name: node!.data.label as string,
        description: (node!.data.description as string) ?? '',
        metadata: full?.metadata ?? {},
        bookIds: (node!.data.bookIds as string[]) ?? [],
        seriesIds: (node!.data.seriesIds as string[]) ?? [],
      };
    };

    setEdges(eds => eds.map(e => ({ ...e, selected: e.id === edgeId })));
    setSelectedPanelRelationship({
      rel: {
        entityId: rel.entityId,
        relationshipType: rel.relationshipType,
        description: rel.description ?? '',
        metadata: rel.metadata ?? {},
        directed: rel.directed ?? false,
      },
      source: toPanel(sourceNode),
      target: toPanel(targetNode),
    });
    setSelectedPanelEntity(null);
    setFocusedNodeId(null);
  }, [setEdges]);

  /* A relationship the server says is already there, which this page's copy lacked (#973):
     taken into the copy and drawn, as the load would have, so it can be opened. */
  const adoptRelationship = useCallback((rel: Relationship) => {
    if (!relationshipsRef.current.some(r => r.entityId === rel.entityId)) {
      relationshipsRef.current = [...relationshipsRef.current, rel];
    }
    deletedHere.current.delete(rel.entityId);
    const [edge] = buildEdges([rel], openEdgePanel, handleUpdateWaypoint, waypointsRef.current, layoutSettingsRef.current.edgeRouting);
    setEdges(eds => (eds.some(e => e.id === rel.entityId) ? eds : [...eds, edge]));
  }, [setEdges, openEdgePanel, handleUpdateWaypoint]);

  // ── Panel-based entity creation ───────────────────────────────────────────

  const handlePanelEntityCreate = useCallback(async (
    entityType: 'character' | 'location' | 'event',
    name: string,
  ) => {
    const preBookIds = scopeParam === 'book' && idParam ? [`BOOK#${idParam}`] : [];
    const preSeriesIds = scopeParam === 'series' && idParam ? [`SERIES#${idParam}`] : [];
    const path = entityType === 'character' ? '/characters'
      : entityType === 'location' ? '/locations'
      : '/events';

    const res = await apiPost(path, {
      name,
      description: '',
      bookIds: preBookIds,
      seriesIds: preSeriesIds,
    });
    const saved = await res.body.json() as unknown as { entityId: string };
    const newId = saved.entityId;

    const titleLookup = new Map<string, string>();
    booksRef.current.forEach(b => titleLookup.set(b.entityId, b.title));
    seriesListRef.current.forEach(s => titleLookup.set(s.entityId, s.title));
    const scopeTags: ScopeTag[] = [];
    for (const id of preBookIds) { const t = titleLookup.get(id); if (t) scopeTags.push({ title: t, kind: 'book' }); }
    for (const id of preSeriesIds) { const t = titleLookup.get(id); if (t) scopeTags.push({ title: t, kind: 'series' }); }

    const i = nodesRef.current.length;
    const cols = Math.max(4, Math.ceil(Math.sqrt(i + 1)));
    const W = 180, H = 110;
    const pos = { x: (i % cols) * W + 40, y: Math.floor(i / cols) * H + 40 };
    setNodes(ns => [...ns, {
      id: newId,
      type: entityType,
      position: pos,
      data: {
        label: name,
        entityType,
        description: '',
        bookIds: preBookIds,
        seriesIds: preSeriesIds,
        scopeTags,
        collapsed: false,
        onEditNode: openNodeEditor,
        onToggleCollapse: handleToggleCollapse,
      },
    }]);

    const newEntity = {
      entityId: newId,
      name,
      description: '',
      metadata: {},
      bookIds: preBookIds,
      seriesIds: preSeriesIds,
    };
    if (entityType === 'character') {
      charactersRef.current = [...charactersRef.current, newEntity as Character];
    } else if (entityType === 'location') {
      locationsRef.current = [...locationsRef.current, newEntity as Location];
    } else {
      eventsRef.current = [...eventsRef.current, newEntity as Event];
    }

    // They just made this deliberately. If its type is filtered out it will not appear,
    // and silence here is how someone concludes creation is broken. (#754)
    const hiddenNotice = createdWhileHiddenMessage(name, entityType, typeVisibility);
    if (hiddenNotice) setCreatedHidden({ message: hiddenNotice, kind: entityType });

    // Transition panel from create mode to edit mode in place
    setSelectedPanelEntity(prev => prev ? {
      ...prev,
      entityId: newId,
      name,
      bookIds: preBookIds,
      seriesIds: preSeriesIds,
    } : prev);
  }, [scopeParam, idParam, setNodes, openNodeEditor, handleToggleCollapse, typeVisibility]);

  // ── Load entities ─────────────────────────────────────────────────────────

  const loadEntities = useCallback(async (scope: 'book' | 'series' | null, id: string, seriesIds: string[] = [], wholeView = true) => {
    // The newest load wins: a slower, older one (a quick switch toggle) must not overwrite it.
    const seq = ++loadSeq.current;
    layoutReadyFor.current = null;
    setLoading(true);
    setError('');
    setLayoutReadFailed(false);
    setFocusedNodeId(null);
    autoSelectedRef.current = false;
    setNodes([]);
    setEdges([]);

    try {
      // Scope is a query filter on the global route, NOT a nested path. `/books/{id}/entities`
      // and `/series/{id}/entities` have never existed in the API — only `/entities` does,
      // which takes ?bookId= / ?seriesId=. API Gateway answers an unknown route without CORS
      // headers, so the browser reported this as a CORS failure rather than a 404 and the
      // canvas silently came up blank for six months. (#751)
      //
      // The filter value is the PREFIXED id, because that is what the bookIds/seriesIds
      // arrays store (see preBookIds below); the URL param is the bare uuid.
      const entitiesPath = scopedEntitiesPath(scope, id, seriesIds);

      const lKey = id || 'global';
      const [entitiesRes, layoutRes] = await Promise.all([
        apiGet(entitiesPath),
        apiGet(`/layouts/${lKey}`).catch(() => null),
      ]);

      const raw = await entitiesRes.body.json() as unknown as {
        characters?: Character[];
        locations?: Location[];
        events?: Event[];
        relationships?: Relationship[];
      };
      const characters = raw.characters ?? [];
      const locations = raw.locations ?? [];
      const events = raw.events ?? [];
      const relationships = raw.relationships ?? [];

      const layoutData = layoutRes ? await layoutRes.body.json() as unknown as { positions?: Record<string, { x: number; y: number }>; waypoints?: Record<string, { x: number; y: number }>; settings?: Partial<LayoutSettings> } : {};
      if (seq !== loadSeq.current) return;
      charactersRef.current = characters;
      locationsRef.current = locations;
      eventsRef.current = events;
      const savedPositions = layoutData?.positions ?? {};
      // A view holding every entry of its layout drops positions and bends of entries and
      // links deleted elsewhere; they would otherwise build up. A partial view keeps them all.
      savedPositionsRef.current = wholeView
        ? pruneLayout(savedPositions, new Set([...characters, ...locations, ...events].map(e => e.entityId)))
        : savedPositions;
      // Only a layout actually read: a failed read (null) must not look like an empty record.
      if (layoutRes) layoutReadyFor.current = lKey;
      else setLayoutReadFailed(true);
      waypointsRef.current = wholeView
        ? pruneLayout(layoutData?.waypoints ?? {}, new Set(relationships.map(r => r.entityId)))
        : layoutData?.waypoints ?? {};

      // Restore settings from backend (cross-device) if present, otherwise keep local settings
      const effectiveRouting = layoutData?.settings?.edgeRouting ?? layoutSettingsRef.current.edgeRouting;
      if (layoutData?.settings) {
        setLayoutSettings(prev => ({ ...DEFAULT_LAYOUT, ...prev, ...layoutData.settings }));
      }

      relationshipsRef.current = relationships;
      relationships.forEach(r => deletedHere.current.delete(r.entityId));
      const bookTitleMap = new Map<string, string>();
      const seriesTitleMap = new Map<string, string>();
      booksRef.current.forEach(b => bookTitleMap.set(b.entityId, b.title));
      seriesListRef.current.forEach(s => seriesTitleMap.set(s.entityId, s.title));
      setNodes(buildNodes(characters, locations, events, savedPositions, openNodeEditor, handleToggleCollapse, bookTitleMap, seriesTitleMap));
      setEdges(buildEdges(relationships, openEdgePanel, handleUpdateWaypoint, waypointsRef.current, effectiveRouting, savedPositions));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      console.error('Failed to load entities:', e);
      setError('Failed to load entities. Check the browser console for details.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [setLoading, setError, setFocusedNodeId, setNodes, setEdges, setLayoutSettings, openNodeEditor, handleToggleCollapse, openEdgePanel, handleUpdateWaypoint]);

  /* A book or series filter change reloads the entities, and a panel left open would show
     one the page may no longer hold: its edits went nowhere while it said "Saved", and its
     ticked books and series could be out of date (#974). Both panels close first, which
     sends any edits they are waiting on, and the reload starts a moment later, once that
     has happened, so those edits still find the entity they were made on. A reload for any
     other reason leaves the panels as they are. Turning the series switch counts as a
     filter change (#1138). */
  const loadedFilter = useRef<string | null>(null);
  useEffect(() => {
    if (awaitingSeries) return;
    // From the key string, not the array: the array is new whenever the series list arrives,
    // which would reload an unchanged view a second time.
    const seriesIds = includedSeriesKey ? includedSeriesKey.split(',') : [];
    const filter = filterKey(scopeParam, idParam, seriesIds);
    const filterChanged = loadedFilter.current !== null && loadedFilter.current !== filter;
    loadedFilter.current = filter;
    const load = () => {
      loadEntities(scopeParam, idParam, seriesIds, wholeLayout);
      layoutKey.current = idParam || 'global';
    };
    // A drag save still waiting is sent before any reload, whatever caused it.
    flushPendingLayoutSave();
    if (!filterChanged) { load(); return; }
    setSelectedPanelEntity(null);
    setSelectedPanelRelationship(null);
    setFocusedNodeId(null);
    const timer = setTimeout(load, 0);
    return () => clearTimeout(timer);
  }, [scopeParam, idParam, includedSeriesKey, wholeLayout, awaitingSeries, layoutRetry, loadEntities, flushPendingLayoutSave]);

  const didFitRef = useRef(false);
  useEffect(() => {
    if (nodes.length > 0 && rfInstance.current && !didFitRef.current) {
      didFitRef.current = true;
      rfInstance.current.fitView({ padding: 0.2 });
    }
    if (nodes.length === 0) didFitRef.current = false;
  }, [nodes.length]);

  // ── Auto-save node positions ───────────────────────────────────────────────

  const handleNodesChange = useCallback((changes: Parameters<typeof onNodesChange>[0]) => {
    onNodesChange(changes);
    const hasMoved = changes.some((c: { type: string; dragging?: boolean }) => c.type === 'position' && !c.dragging);
    if (!hasMoved) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; saveCanvasLayout(); }, 1000);
  }, [onNodesChange, saveCanvasLayout]);

  // ── Connect nodes ─────────────────────────────────────────────────────────

  const handleConnect = useCallback(async (connection: Connection) => {
    if (!connection.source || !connection.target) return;
    // Already linked this way round: open that relationship rather than reset it. (#966)
    const existing = relationshipBetween(relationshipsRef.current, connection.source, connection.target);
    if (existing) { openEdgePanel(existing.entityId); return; }
    try {
      const res = await apiPost('/relationships', {
        sourceId: connection.source,
        targetId: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
        relationshipType: 'related',
        description: '',
        // Refused if the link is there already, though this page's copy did not know. (#973)
        createOnly: true,
      });
      const saved = await res.body.json() as { entityId: string };
      const newRel: Relationship = {
        entityId: saved.entityId,
        sourceId: connection.source,
        targetId: connection.target,
        sourceHandle: connection.sourceHandle ?? undefined,
        targetHandle: connection.targetHandle ?? undefined,
        relationshipType: 'related',
        description: '',
      };
      relationshipsRef.current = [...relationshipsRef.current, newRel];
      deletedHere.current.delete(newRel.entityId);
      const newEdge: Edge = {
        id: saved.entityId,
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle ?? null,
        targetHandle: connection.targetHandle ?? null,
        type: 'relationship',
        style: { stroke: '#196773', strokeWidth: 1.5 },
        data: { label: 'related', description: '', onEdit: openEdgePanel, onUpdateWaypoint: handleUpdateWaypoint, waypoint: null, routing: layoutSettingsRef.current.edgeRouting },
      };
      setEdges(eds => addEdge(newEdge, eds));
      openEdgePanel(saved.entityId);
    } catch (e) {
      const inTheWay = await relationshipInTheWay(e);
      if (inTheWay) { adoptRelationship(inTheWay); openEdgePanel(inTheWay.entityId); return; }
      console.error('Failed to save relationship:', e);
    }
  }, [setEdges, openEdgePanel, handleUpdateWaypoint, adoptRelationship]);

  // ── Delete relationship ───────────────────────────────────────────────────

  const deleteRelationship = useCallback(async (edgeId: string) => {
    // Optimistic — remove from UI immediately before the network call
    setEdges(eds => eds.filter(e => e.id !== edgeId));
    deletedHere.current.add(edgeId);
    relationshipsRef.current = relationshipsRef.current.filter(r => r.entityId !== edgeId);
    const urlId = bareId(edgeId);
    await apiDel(`/relationships/${urlId}`);
  }, [setEdges]);

  /* Set while a delete that includes NODES is in flight. ReactFlow hands the doomed
     node's connected edges to onEdgesDelete as well, but DELETE /characters/{id}
     already cascades to relationships server-side (platform/characters.js:90), so
     deleting them from here too is redundant, costs N+1 round trips, and races: if the
     entity delete wins, every per-edge DELETE 404s and is logged as a failure for an
     operation that entirely succeeded.
     Leaving them to the cascade also closes the worse hole — relationships used to be
     destroyed before the entity delete had even been attempted, so a failure left the
     user with exactly the bug this issue is about. (#785) */
  const deletingNodes = useRef(false);

  const handleEdgesDelete = useCallback(async (deleted: Edge[]) => {
    if (deletingNodes.current) return;
    for (const edge of deleted) {
      try { await deleteRelationship(edge.id); } catch (e) { console.error(e); }
    }
  }, [deleteRelationship]);

  /* Delete used to remove a node from the canvas and nothing else, because this
     handler did not exist. ReactFlow still passed the connected edges to
     onEdgesDelete, which does delete server-side — so one keystroke destroyed every
     relationship the user had authored and left the entity behind. Reload and the
     character was back, stripped of its connections, with nothing having reported a
     failure. (#785) */
  /* Saves read these copies, so a late save for an entity that is gone (edits a panel
     sends as it closes, say) finds nothing and, being for something deleted here, is
     dropped quietly rather than writing it back: the POST is an upsert. (#974) The
     entity's relationships go with it, as they do on the server. The entity lists also
     back the relate pickers in the side panel, where a deleted entity would stay
     selectable until a reload. (#965) */
  const forgetEntities = useCallback((gone: Set<string>) => {
    gone.forEach(id => deletedHere.current.add(id));
    relationshipsRef.current.forEach(r => {
      if (gone.has(r.sourceId) || gone.has(r.targetId)) deletedHere.current.add(r.entityId);
    });
    charactersRef.current = charactersRef.current.filter(c => !gone.has(c.entityId));
    locationsRef.current = locationsRef.current.filter(l => !gone.has(l.entityId));
    eventsRef.current = eventsRef.current.filter(e => !gone.has(e.entityId));
    relationshipsRef.current = relationshipsRef.current.filter(r => !gone.has(r.sourceId) && !gone.has(r.targetId));
  }, []);

  const handleNodesDelete = useCallback(async (deleted: Node[]) => {
    const gone = new Set(deleted.map(n => n.id));
    /* Before the DELETE is awaited: a panel's edit still waiting on its one-second timer
       (overdue by now, after the confirm dialog) fires during it, and must find nothing
       to save. If the delete fails, the nodes are off the canvas already and the message
       below says to reload. */
    forgetEntities(gone);
    try {
      const failed = await deleteEntities(deleted.map(n => n.id), apiDel);
      if (failed.length) {
        // ReactFlow has already taken these off the canvas and will not put them back,
        // so without this the UI reports a delete that did not happen.
        setError(failed.length === 1
          ? 'Could not delete that entity. Reload to see what is actually there.'
          : `Could not delete ${failed.length} entities. Reload to see what is actually there.`);
      }
    } finally {
      deletingNodes.current = false;
    }

    setSelectedPanelEntity(sel => (sel && gone.has(sel.entityId) ? null : sel));
    setSelectedPanelRelationship(sel =>
      (sel && (gone.has(sel.source.entityId) || gone.has(sel.target.entityId)) ? null : sel));
    /* Focus lights the focused node and its connections. Its edges are gone too, so a
       stale id lights nothing and drops every surviving node to 15% opacity until the
       user finds the Clear focus button. */
    setFocusedNodeId(cur => (cur && gone.has(cur) ? null : cur));
  }, [forgetEntities]);

  /* Deleting an entity is irreversible and takes its relationships with it, so the
     canvas asks — the panel's own delete button has always been a two-step confirm,
     and a bare keypress should not be the cheaper way to do more damage.

     window.confirm rather than the inline two-step used elsewhere: onBeforeDelete wants
     a promise resolving to a decision, and being synchronous it also closes the door on
     a second Delete re-entering deleteElements mid-decision. If this is ever replaced
     by a promise-bridged modal that re-entrancy becomes real and needs guarding. It
     also fails safe — a sandboxed frame without allow-modals returns false, vetoing.

     Edges alone are not gated: that is today's behaviour, and a relationship is a
     couple of clicks to recreate. Returning false vetoes the whole deletion. */
  const handleBeforeDelete = useCallback(async (
    { nodes: toDelete, edges: doomedEdges }: { nodes: Node[]; edges: Edge[] },
  ) => {
    if (toDelete.length === 0) return true;
    // eslint-disable-next-line no-alert
    if (!window.confirm(deleteConfirmMessage(toDelete, doomedEdges))) return false;
    // Only once the user has said yes: a vetoed delete must not leave this set, or the
    // next edge-only delete would skip its server call.
    deletingNodes.current = true;
    return true;
  }, []);

  // ── Re-create an existing relationship under new endpoints ────────────────

  // Shared by the reconnect and swap-direction paths. Both must re-CREATE rather
  // than PUT, because entityId is md5(sourceId + targetId) — moving an endpoint
  // necessarily changes the key.
  //
  // POST is create-with-defaults on the server: any field omitted here is silently
  // reset (metadata -> {}, directed -> false). That is exactly how the reconnect
  // path used to destroy user-authored relationship metadata, so every carried
  // field is listed explicitly. Keep both callers going through this helper so the
  // two paths cannot drift apart again.
  const recreateRelationship = useCallback(async (
    rel: Relationship,
    next: {
      sourceId: string;
      targetId: string;
      sourceHandle?: string | null;
      targetHandle?: string | null;
      directed?: boolean;
    },
    // Set when the key changes, so a link already at the new key is refused, not
    // overwritten (#973). A same-key rewrite is an in-place update and must not be.
    createOnly = false,
  ): Promise<string> => {
    const res = await apiPost('/relationships', {
      sourceId: next.sourceId,
      targetId: next.targetId,
      sourceHandle: next.sourceHandle ?? null,
      targetHandle: next.targetHandle ?? null,
      relationshipType: rel.relationshipType,
      description: rel.description ?? '',
      metadata: rel.metadata ?? {},
      directed: next.directed ?? rel.directed ?? false,
      ...(createOnly ? { createOnly: true } : {}),
    });
    const saved = await res.body.json() as unknown as { entityId: string };
    return saved.entityId;
  }, []);

  // ── Reconnect edge to a different handle ──────────────────────────────────

  const handleReconnect = useCallback(async (oldEdge: Edge, newConnection: Connection) => {
    const source = newConnection.source;
    const target = newConnection.target;
    if (!source || !target) return;

    const rel = relationshipsRef.current.find(r => r.entityId === oldEdge.id);
    if (!rel) {
      // The edge exists on the canvas but not in our data. Re-creating it would
      // invent a relationship with default metadata, so refuse rather than write
      // a degraded row — the drag visibly snaps back, which is the honest signal.
      console.error('[handleReconnect] no relationship found for edge', oldEdge.id);
      return;
    }

    // Another relationship already joins the new pair this way round, and writing this
    // one there would overwrite it. The edge snaps back, and that one opens. (#966)
    const existing = relationshipBetween(relationshipsRef.current, source, target, rel.entityId);
    if (existing) { openEdgePanel(existing.entityId); return; }

    // Optimistically update the edge in React Flow state, keeping its id: React Flow gives
    // the moved edge a generated one by default, and then neither the snap-back on a failure
    // nor the swap to the saved id below could find it. (#973)
    setEdges(eds => reconnectEdge(oldEdge, newConnection, eds, { shouldReplaceId: false }));

    // Unchanged endpoints (a handle-only reconnect) keep the same md5 key, so the
    // POST below is an in-place upsert and there is nothing to delete — issuing the
    // DELETE would destroy the row we just wrote.
    const sameEndpoints = source === rel.sourceId && target === rel.targetId;
    let newEntityId: string | null = null;

    try {
      // POST before DELETE so a failed write can never leave the relationship gone.
      newEntityId = await recreateRelationship(rel, {
        sourceId: source,
        targetId: target,
        sourceHandle: newConnection.sourceHandle,
        targetHandle: newConnection.targetHandle,
      }, !sameEndpoints);

      if (!sameEndpoints) {
        await apiDel(`/relationships/${bareId(oldEdge.id)}`);
      }

      deletedHere.current.delete(newEntityId);
      relationshipsRef.current = [
        ...relationshipsRef.current.filter(r => r.entityId !== oldEdge.id),
        {
          ...rel,
          entityId: newEntityId,
          sourceId: source,
          targetId: target,
          sourceHandle: newConnection.sourceHandle ?? undefined,
          targetHandle: newConnection.targetHandle ?? undefined,
        },
      ];
      // Swap the old id for the new server-assigned id
      const assignedId = newEntityId;
      setEdges(eds => eds.map(e => e.id === oldEdge.id ? { ...e, id: assignedId } : e));
    } catch (e) {
      // The new pair is linked already, though this page's copy did not know (#973): the
      // edge snaps back, nothing was written, and that one opens.
      const inTheWay = newEntityId ? null : await relationshipInTheWay(e);
      if (!inTheWay) console.error('Failed to reconnect edge:', e);
      // If the POST landed but the DELETE failed we would otherwise leave a
      // duplicate behind. Only safe when the key actually changed.
      if (newEntityId && !sameEndpoints) {
        apiDel(`/relationships/${bareId(newEntityId)}`).catch(() => {
          // compensating delete also failed — nothing further we can do
        });
      }
      setEdges(eds => eds.map(e => e.id === oldEdge.id ? oldEdge : e));
      if (inTheWay) { adoptRelationship(inTheWay); openEdgePanel(inTheWay.entityId); }
    }
  }, [setEdges, recreateRelationship, openEdgePanel, adoptRelationship]);

  // ── Entity panel save / delete ────────────────────────────────────────────────

  /* Saves of one entity or relationship run one at a time, each built when it runs from
     the copy the save before it left. A save sends every field, filling the ones its edit
     did not touch from that copy, and the copy is only updated once the POST returns, so
     a save that started while another was in flight sent the other's change back to its
     old value: a Role picked while the description was still saving, say. Swapping a
     relationship's direction rewrites it from the same copy, so it queues too. (#955)
     State rather than useMemo: React may drop a memo, and that would reset the queue. */
  const [serialSave] = useState(serializeByKey);

  const saveEntity = useCallback(async (entityId: string, updates: EntityUpdate) => {
    const entityType = entityKind(entityId);
    const path = collectionPath(entityType);
    const arr = entityType === 'character' ? charactersRef.current
      : entityType === 'location' ? locationsRef.current
      : eventsRef.current;
    const existing = arr.find(e => e.entityId === entityId);
    if (!existing) return saveForMissingRecord(entityId, deletedHere.current);

    const payload = {
      entityId,
      // An emptied name is not saved (the server keeps the old one too); a panel closed
      // mid-rename sends what was typed so far. (#965)
      name: updates.name?.trim() || existing.name,
      description: updates.description ?? existing.description ?? '',
      imageUrl: updates.imageUrl ?? existing.imageUrl ?? '',
      bookIds: updates.bookIds ?? existing.bookIds ?? [],
      seriesIds: updates.seriesIds ?? existing.seriesIds ?? [],
      metadata: updates.metadata !== undefined
        ? { ...(existing.metadata ?? {}), ...updates.metadata }
        : existing.metadata ?? {},
    };

    await apiPost(path, { ...payload, ...scopeRemovals(updates) });

    const updated = { ...existing, ...payload };
    if (entityType === 'character') {
      charactersRef.current = charactersRef.current.map(e => e.entityId === entityId ? updated as Character : e);
    } else if (entityType === 'location') {
      locationsRef.current = locationsRef.current.map(e => e.entityId === entityId ? updated as Location : e);
    } else {
      eventsRef.current = eventsRef.current.map(e => e.entityId === entityId ? updated as Event : e);
    }

    const bookTitleMap = new Map<string, string>();
    const seriesTitleMap = new Map<string, string>();
    booksRef.current.forEach(b => bookTitleMap.set(b.entityId, b.title));
    seriesListRef.current.forEach(s => seriesTitleMap.set(s.entityId, s.title));
    const scopeTags: ScopeTag[] = [];
    for (const id of payload.bookIds) { const t = bookTitleMap.get(id); if (t) scopeTags.push({ title: t, kind: 'book' }); }
    for (const id of payload.seriesIds) { const t = seriesTitleMap.get(id); if (t) scopeTags.push({ title: t, kind: 'series' }); }

    setNodes(ns => ns.map(n => n.id === entityId
      ? { ...n, data: { ...n.data, label: payload.name, description: payload.description, bookIds: payload.bookIds, seriesIds: payload.seriesIds, scopeTags } }
      : n
    ));
    setSelectedPanelEntity(prev => prev?.entityId === entityId
      ? { ...prev, ...updates, imageUrl: payload.imageUrl, metadata: payload.metadata }
      : prev
    );
  }, [setNodes]);

  const handlePanelEntitySave = useCallback(
    (entityId: string, updates: EntityUpdate) => serialSave(entityId, () => saveEntity(entityId, updates)),
    [serialSave, saveEntity],
  );

  const handlePanelImageUploaded = useCallback((entityId: string, viewUrl: string) => {
    setNodes(ns => ns.map(n => n.id === entityId
      ? { ...n, data: { ...n.data, imageUrl: viewUrl } }
      : n
    ));
  }, [setNodes]);

  const handlePanelEntityDelete = useCallback(async (entityId: string) => {
    const path = entityPath(entityId);
    try {
      await apiDel(path);
      forgetEntities(new Set([entityId]));
      setNodes(ns => ns.filter(n => n.id !== entityId));
      setEdges(es => es.filter(e => e.source !== entityId && e.target !== entityId));
      setSelectedPanelEntity(null);
    } catch (err) {
      console.error('Failed to delete entity', err);
    }
  }, [forgetEntities, setNodes, setEdges]);

  // ── Relationship panel save / delete ─────────────────────────────────────────

  const saveRelationship = useCallback(async (relId: string, updates: RelationshipUpdate) => {
    const rel = relationshipsRef.current.find(r => r.entityId === relId);
    if (!rel) return saveForMissingRecord(relId, deletedHere.current);

    const payload = relationshipSavePayload(rel, updates);

    await apiPost('/relationships', payload);

    relationshipsRef.current = relationshipsRef.current.map(r =>
      r.entityId === relId
        ? { ...r, relationshipType: payload.relationshipType, description: payload.description, metadata: payload.metadata, directed: payload.directed }
        : r
    );

    if (updates.relationshipType) {
      setEdges(eds => eds.map(e =>
        e.id === relId ? { ...e, data: { ...e.data, label: payload.relationshipType } } : e
      ));
    }
    if (updates.directed !== undefined) {
      setEdges(eds => eds.map(e =>
        e.id === relId ? { ...e, data: { ...e.data, directed: payload.directed } } : e
      ));
    }
    // A queued save can land after the panel has moved on to another relationship.
    setSelectedPanelRelationship(prev => prev?.rel.entityId === relId
      ? { ...prev, rel: { ...prev.rel, ...updates, metadata: payload.metadata } }
      : prev
    );
  }, [setEdges]);

  const handlePanelRelSave = useCallback(
    (relId: string, updates: RelationshipUpdate) => serialSave(relId, () => saveRelationship(relId, updates)),
    [serialSave, saveRelationship],
  );

  const handlePanelRelDelete = useCallback(async (relId: string) => {
    try {
      await deleteRelationship(relId);
      setSelectedPanelRelationship(null);
    } catch (err) {
      console.error('Failed to delete relationship', err);
    }
  }, [deleteRelationship]);

  const handleSwapDirection = useCallback((relId: string) => serialSave(relId, async () => {
    if (swappingRef.current) return;
    swappingRef.current = true;

    const rel = relationshipsRef.current.find(r => r.entityId === relId);
    if (!rel) { swappingRef.current = false; return; }

    // A relationship already runs the other way between these two, and swapping would
    // overwrite it. Open that one instead. (#966)
    const existing = relationshipBetween(relationshipsRef.current, rel.targetId, rel.sourceId, rel.entityId);
    if (existing) { swappingRef.current = false; openEdgePanel(existing.entityId); return; }

    let newEntityId: string | null = null;
    try {
      // POST new relationship with swapped source/target — produces a different entityId
      const savedId = await recreateRelationship(rel, {
        sourceId: rel.targetId,
        targetId: rel.sourceId,
        sourceHandle: rel.targetHandle ?? null,
        targetHandle: rel.sourceHandle ?? null,
        directed: true,
      }, rel.sourceId !== rel.targetId);
      newEntityId = savedId;

      // DELETE old relationship. Not for a link from a card to itself: swapping its ends
      // gives the same key, so the POST was an in-place upsert and the DELETE would
      // destroy the row just written (as handleReconnect's sameEndpoints guards). (#966)
      if (savedId !== relId) {
        const oldUrlId = bareId(relId);
        await apiDel(`/relationships/${oldUrlId}`);
      }

      // Swap in relationshipsRef
      const newRel: Relationship = {
        ...rel,
        entityId: savedId,
        sourceId: rel.targetId,
        targetId: rel.sourceId,
        sourceHandle: rel.targetHandle,
        targetHandle: rel.sourceHandle,
        directed: true,
      };
      relationshipsRef.current = [
        ...relationshipsRef.current.filter(r => r.entityId !== relId),
        newRel,
      ];
      deletedHere.current.delete(newRel.entityId);

      // Swap the ReactFlow edge: remove old, add new with flipped source/target
      setEdges(eds => {
        const old = eds.find(e => e.id === relId);
        if (!old) return eds;
        return [
          ...eds.filter(e => e.id !== relId),
          {
            ...old,
            id: savedId,
            source: rel.targetId,
            target: rel.sourceId,
            sourceHandle: rel.targetHandle ?? null,
            targetHandle: rel.sourceHandle ?? null,
            data: { ...old.data, directed: true },
          },
        ];
      });

      // Re-open panel with new entityId
      openEdgePanel(savedId);
    } catch (err) {
      // A link already runs the other way, though this page's copy did not know (#973):
      // nothing was written, this one stays as it was, and that one opens.
      const inTheWay = newEntityId ? null : await relationshipInTheWay(err);
      if (inTheWay) {
        adoptRelationship(inTheWay);
        openEdgePanel(inTheWay.entityId);
      } else {
        console.error('[handleSwapDirection] swap failed', err);
      }
      // If POST succeeded but DELETE failed, attempt compensating delete to avoid duplicate
      if (newEntityId && newEntityId !== relId) {
        const compensatingId = bareId(newEntityId);
        apiDel(`/relationships/${compensatingId}`).catch(() => {
          // compensating delete also failed — data inconsistency, nothing more to do
        });
      }
    } finally {
      swappingRef.current = false;
    }
  }), [serialSave, setEdges, openEdgePanel, recreateRelationship, adoptRelationship]);

  // ── Auto-create relationship when event participant is added ──────────────────

  const handleAutoRelate = useCallback(async (eventId: string, targetId: string, relType: string) => {
    const exists = relationshipsRef.current.some(r =>
      (r.sourceId === eventId && r.targetId === targetId) ||
      (r.sourceId === targetId && r.targetId === eventId)
    );
    if (exists) return;

    const res = await apiPost('/relationships', { sourceId: eventId, targetId, relationshipType: relType, description: '' });
    const saved = await res.body.json() as { entityId: string };
    const newRel: Relationship = { entityId: saved.entityId, sourceId: eventId, targetId, relationshipType: relType, description: '' };
    relationshipsRef.current = [...relationshipsRef.current, newRel];
    deletedHere.current.delete(newRel.entityId);
    setEdges(eds => addEdge({
      id: saved.entityId,
      source: eventId,
      target: targetId,
      type: 'relationship',
      style: { stroke: '#196773', strokeWidth: 1.5 },
      data: { label: relType, description: '', onEdit: openEdgePanel, onUpdateWaypoint: handleUpdateWaypoint, waypoint: null, routing: layoutSettingsRef.current.edgeRouting },
    }, eds));
  }, [setEdges, openEdgePanel, handleUpdateWaypoint]);

  // ── Auto layout (dagre, per connected component) ────────────────────────

  const handleAutoLayout = useCallback(() => {
    const NODE_W = 230, NODE_H = 200;
    const GAP = 80;
    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    if (!nodes.length) return;

    const { rankdir, spacing } = layoutSettingsRef.current;
    const { nodesep, ranksep } = SPACING_MAP[spacing];

    // Real rendered height per card (cards are a fixed 230px wide in CSS, but their
    // height varies with content). Fall back to the constant before ReactFlow has
    // measured them. Using true heights keeps dagre from under-spacing tall cards.
    const nodeById = new Map(nodes.map(n => [n.id, n]));
    const heightOf = (id: string) => nodeById.get(id)?.measured?.height ?? NODE_H;

    // Build undirected adjacency to find connected components
    const adj = new Map<string, string[]>();
    nodes.forEach(n => adj.set(n.id, []));
    edges.forEach(e => {
      adj.get(e.source as string)?.push(e.target as string);
      adj.get(e.target as string)?.push(e.source as string);
    });

    // BFS to collect components
    const seen = new Set<string>();
    const components: string[][] = [];
    for (const n of nodes) {
      if (seen.has(n.id)) continue;
      const comp: string[] = [];
      const q = [n.id];
      seen.add(n.id);
      while (q.length) {
        const id = q.shift()!;
        comp.push(id);
        for (const nb of adj.get(id) ?? []) {
          if (!seen.has(nb)) { seen.add(nb); q.push(nb); }
        }
      }
      components.push(comp);
    }

    const isolated = components.filter(c => c.length === 1).map(c => c[0]);
    const connected = components.filter(c => c.length > 1);
    const positions: Record<string, { x: number; y: number }> = {};

    // Isolated nodes: single row at the top
    isolated.forEach((id, i) => {
      positions[id] = { x: i * (NODE_W + GAP), y: 0 };
    });
    const isoRowH = isolated.length ? NODE_H + GAP : 0;

    // Each connected component gets its own dagre run
    let offsetX = 0;
    for (const comp of connected) {
      const compSet = new Set(comp);
      const g = new dagre.graphlib.Graph();
      g.setDefaultEdgeLabel(() => ({}));
      g.setGraph({ rankdir, nodesep, ranksep, marginx: 30, marginy: 30, acyclicer: 'greedy' });
      comp.forEach(id => g.setNode(id, { width: NODE_W, height: heightOf(id) }));
      edges
        .filter(e => compSet.has(e.source as string) && compSet.has(e.target as string))
        .forEach(e => g.setEdge(e.source as string, e.target as string));
      dagre.layout(g);

      // Shift any node that sits in the direct path of a long edge (spans 2+ ranks).
      // Without this, e.g. Sil→Wither passes straight through Caiga when all three
      // are in the same column of a TB layout.
      const nodeRanks: Record<string, number> = {};
      comp.forEach(id => { nodeRanks[id] = (g.node(id) as { rank?: number }).rank ?? 0; });
      const compEdges = edges.filter(e => compSet.has(e.source as string) && compSet.has(e.target as string));
      compEdges.forEach(e => {
        const srcRank = nodeRanks[e.source as string] ?? 0;
        const tgtRank = nodeRanks[e.target as string] ?? 0;
        if (Math.abs(tgtRank - srcRank) <= 1) return;
        const minRank = Math.min(srcRank, tgtRank);
        const maxRank = Math.max(srcRank, tgtRank);
        const srcNode = g.node(e.source as string);
        const perpCoord = rankdir === 'TB' ? srcNode.x : srcNode.y;
        const nodeSize  = rankdir === 'TB' ? NODE_W : NODE_H;
        comp.forEach(id => {
          const r = nodeRanks[id];
          if (r <= minRank || r >= maxRank) return;
          const nd = g.node(id);
          const ndCoord = rankdir === 'TB' ? nd.x : nd.y;
          if (Math.abs(ndCoord - perpCoord) < nodeSize / 2 + 10) {
            if (rankdir === 'TB') nd.x += nodeSize + nodesep;
            else nd.y += nodeSize + nodesep;
          }
        });
      });

      // Safety net: within each rank, enforce a minimum cross-axis gap so cards can
      // never overlap. dagre spaces correctly on its own, but the long-edge shift
      // above can push a node onto its rank neighbour — this greedily separates them
      // (using each card's real height), so nodes never end up stacked.
      {
        const axis: 'x' | 'y' = rankdir === 'TB' ? 'x' : 'y';
        const crossSize = (id: string) => (rankdir === 'TB' ? NODE_W : heightOf(id));
        const byRank = new Map<number, string[]>();
        comp.forEach(id => {
          const r = nodeRanks[id] ?? 0;
          const arr = byRank.get(r);
          if (arr) arr.push(id); else byRank.set(r, [id]);
        });
        byRank.forEach(ids => {
          ids.sort((a, b) => (g.node(a) as Record<'x' | 'y', number>)[axis] - (g.node(b) as Record<'x' | 'y', number>)[axis]);
          for (let i = 1; i < ids.length; i++) {
            const prev = g.node(ids[i - 1]) as Record<'x' | 'y', number>;
            const cur = g.node(ids[i]) as Record<'x' | 'y', number>;
            const need = prev[axis] + crossSize(ids[i - 1]) / 2 + crossSize(ids[i]) / 2 + nodesep;
            if (cur[axis] < need) cur[axis] = need;
          }
        });
      }

      // Normalise to a (0,0) origin. dagre reports node CENTRES, but React Flow
      // positions are top-left — with variable card heights we must subtract each
      // node's own half-height, otherwise a shorter card below a taller one
      // re-overlaps. (Width is a fixed 230px, so its half cancels out.)
      let minX = Infinity, minY = Infinity, maxX = -Infinity;
      comp.forEach(id => {
        const nd = g.node(id) as Record<'x' | 'y', number>;
        const left = nd.x - NODE_W / 2;
        const top = nd.y - heightOf(id) / 2;
        if (left < minX) minX = left;
        if (top < minY) minY = top;
        if (left + NODE_W > maxX) maxX = left + NODE_W;
      });
      const compW = maxX - minX;
      const snapX = offsetX;

      comp.forEach(id => {
        const nd = g.node(id) as Record<'x' | 'y', number>;
        const left = nd.x - NODE_W / 2;
        const top = nd.y - heightOf(id) / 2;
        positions[id] = { x: snapX + (left - minX), y: isoRowH + (top - minY) };
      });
      offsetX += compW + GAP;
    }

    const laid = nodes.map(n => ({ ...n, position: positions[n.id] ?? n.position }));
    setNodes(laid);

    // Spread relationship labels apart so they don't stack on top of each other.
    const centers = new Map(laid.map(n => [n.id, {
      x: n.position.x + NODE_W / 2,
      y: n.position.y + heightOf(n.id) / 2,
    }]));
    const labelOffsets = resolveLabelOffsets(
      centers,
      edges as unknown as Array<{ id: string; source: string; target: string; data?: Record<string, unknown> }>,
    );

    // Assign handles aligned with the computed layout direction so edges don't cross through nodes
    setEdges(prev => prev.map(e => {
      const labelOffset = labelOffsets.get(e.id) ?? null;
      const sp = positions[e.source as string];
      const tp = positions[e.target as string];
      if (!sp || !tp) return { ...e, sourceHandle: null, targetHandle: null, data: { ...(e.data ?? {}), labelOffset } };
      const dx = tp.x - sp.x;
      const dy = tp.y - sp.y;
      let sourceHandle: string, targetHandle: string;
      if (Math.abs(dx) >= Math.abs(dy)) {
        sourceHandle = dx >= 0 ? `${e.source}-r` : `${e.source}-l`;
        targetHandle = dx >= 0 ? `${e.target}-l` : `${e.target}-r`;
      } else {
        sourceHandle = dy >= 0 ? `${e.source}-b` : `${e.source}-t`;
        targetHandle = dy >= 0 ? `${e.target}-t` : `${e.target}-b`;
      }
      return { ...e, sourceHandle, targetHandle, data: { ...(e.data ?? {}), labelOffset } };
    }));
    setTimeout(() => rfInstance.current?.fitView({ padding: 0.15, duration: 300 }), 50);
    const posMap: Record<string, { x: number; y: number }> = {};
    laid.forEach(n => { posMap[n.id] = n.position; });
    saveLayout(posMap);
  }, [setNodes, setEdges, saveLayout]);

  // ── Vocab update ──────────────────────────────────────────────────────────

  const handleVocabUpdate = async (updated: VocabData) => {
    setVocab(updated);
    try {
      await apiPut('/vocab', updated as unknown as Record<string, unknown>);
    } catch (err) {
      console.error('Error saving vocab:', err);
      try {
        const res = await apiGet('/vocab');
        setVocab(await res.body.json() as unknown as VocabData);
      } catch { /* ignore */ }
    }
  };

  // ── Scope filter ──────────────────────────────────────────────────────────

  // Read-only stays read-only: the desktop's window and an embed tab must not turn writable (#1034).
  const handleScopeChange = (scope: 'global' | 'book' | 'series', id?: string) => {
    if (scope === 'global') { setSearchParams(keepReadonly({}, searchParams)); }
    else if (id) { setSearchParams(keepReadonly({ scope, id }, searchParams)); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const roEntityInfo: ReadOnlyEntityInfo | null = isReadonly && selectedPanelEntity
    ? { kind: 'entity', entity: selectedPanelEntity, openUrl: buildOpenUrl(selectedPanelEntity.entityId) }
    : null;

  const roRelInfo: ReadOnlyRelationshipInfo | null = isReadonly && selectedPanelRelationship
    ? {
        kind: 'relationship',
        rel: selectedPanelRelationship.rel,
        source: selectedPanelRelationship.source,
        target: selectedPanelRelationship.target,
        typeColor: REL_TYPE_COLORS[selectedPanelRelationship.rel.relationshipType] ?? '#196773',
        openUrl: buildOpenUrl(selectedPanelRelationship.source.entityId),
      }
    : null;

  return (
    <div className={`relationships-page${isReadonly ? ' readonly' : ''}`} data-theme={embedTheme}>
      <div className="rf-toolbar">
        {(books.length > 0 || seriesList.length > 0) && (
          <div className="rf-filters">
            <span className="rf-filter-label">Filter</span>
            {books.length > 0 && (
              <select
                className="rf-filter-select"
                value={scopeParam === 'book' ? idParam : ''}
                onChange={e => e.target.value ? handleScopeChange('book', e.target.value) : handleScopeChange('global')}
              >
                <option value="">Show all books</option>
                {books.map(b => <option key={b.entityId} value={b.entityId.replace(/^BOOK#/, '')}>{b.title}</option>)}
              </select>
            )}
            {showSwitch && (
              <IncludeSeriesSwitch
                checked={includeSeries}
                onChange={handleIncludeSeries}
                seriesTitles={bookSeries.map(s => s.title)}
              />
            )}
            {seriesList.length > 0 && (
              <select
                className="rf-filter-select"
                value={scopeParam === 'series' ? idParam : ''}
                onChange={e => e.target.value ? handleScopeChange('series', e.target.value) : handleScopeChange('global')}
              >
                <option value="">Show all series</option>
                {seriesList.map(s => <option key={s.entityId} value={s.entityId.replace(/^SERIES#/, '')}>{s.title}</option>)}
              </select>
            )}
          </div>
        )}

        <div className="rf-legend">
          {!isReadonly && (
            <div className="rt-split-btn" ref={splitBtnRef}>
              <button
                className="rt-split-main"
                onClick={() => openNewEntityPanel('character')}
                title="Add character"
              >
                <Plus size={14} /> <span>Character</span>
              </button>
              <button
                className="rt-split-caret"
                onClick={() => setAddMenuOpen(o => !o)}
                aria-label="More entity types"
                aria-expanded={addMenuOpen}
                aria-haspopup="menu"
              >
                <ChevronDown size={12} />
              </button>
              {addMenuOpen && (
                <div className="rt-split-menu" role="menu">
                  <button role="menuitem" onClick={() => { setAddMenuOpen(false); openNewEntityPanel('character'); }}>
                    <Users size={14} color="#196773" /> Character
                  </button>
                  <button role="menuitem" onClick={() => { setAddMenuOpen(false); openNewEntityPanel('location'); }}>
                    <MapPin size={14} color="#5CA383" /> Location
                  </button>
                  <button role="menuitem" onClick={() => { setAddMenuOpen(false); openNewEntityPanel('event'); }}>
                    <Zap size={14} color="#c97a1e" /> Event
                  </button>
                </div>
              )}
            </div>
          )}

          {!isReadonly && (
            <button className="rf-toolbar-btn" onClick={handleAutoLayout} title="Arrange nodes using Dagre layout">
              <Layout size={14} /> Auto Layout
            </button>
          )}

          <button className="rf-toolbar-btn" onClick={handleCollapseAll} title="Collapse all nodes">
            <ChevronsDownUp size={14} /> Collapse All
          </button>
          <button className="rf-toolbar-btn" onClick={handleExpandAll} title="Expand all nodes">
            <ChevronsUpDown size={14} /> Expand All
          </button>

          <div className="rf-search">
            <Search size={13} className="rf-search-icon" />
            <input
              ref={searchInputRef}
              className="rf-search-input"
              type="text"
              placeholder="Search by name…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSearchActive(true); }}
              onFocus={() => { setSearchActive(true); setFocusedNodeId(null); }}
              aria-label="Search entities by name"
            />
            {searchQuery && (
              <button
                className="rf-search-clear"
                onClick={() => { setSearchQuery(''); setSearchActive(false); searchInputRef.current?.blur(); }}
                aria-label="Clear search"
              ><X size={12} /></button>
            )}
          </div>

          <div className="rt-spacer" />

          {isReadonly && (() => {
            const p = new URLSearchParams(searchParams);
            p.delete('readonly');
            const qs = p.toString();
            return (
              <a
                href={`${window.location.origin}${window.location.pathname}${qs ? `?${qs}` : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rf-toolbar-btn"
                style={{ textDecoration: 'none' }}
              >
                Edit ↗
              </a>
            );
          })()}

          {!isReadonly && (
            <button
              className={`rt-settings-toggle${drawer === 'view' ? ' active' : ''}${hidden.filtering ? ' filtering' : ''}`}
              onClick={() => setDrawer(d => d === 'view' ? null : 'view')}
              title={hidden.filtering
                ? `${hidden.hiddenKinds.length} entity type${hidden.hiddenKinds.length === 1 ? '' : 's'} hidden. Open to change.`
                : 'View settings'}
            >
              {/* The icon changes, not just the colour: colour alone is not a signal
                  everyone can read, and this is the only clue that content is missing. */}
              {hidden.filtering ? <EyeOff size={14} /> : <Settings2 size={14} />} <span>View</span>
              {hidden.filtering && (
                <span className="rt-settings-count" aria-hidden="true">{hidden.hiddenKinds.length}</span>
              )}
            </button>
          )}
          {!isReadonly && (
            <button
              className={`rt-settings-toggle${drawer === 'world' ? ' active' : ''}`}
              onClick={() => setDrawer(d => d === 'world' ? null : 'world')}
              title="World vocabulary"
            >
              <Globe2 size={14} /> <span>World</span>
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-banner" style={{ margin: '1rem' }}>{error}</div>}

      {layoutReadFailed && !isReadonly && (
        <div className="rf-filter-bar rf-filter-bar--notice" role="status">
          <span>The saved layout didn't load, so moved cards, bent links and Auto Layout won't be saved until it does.</span>
          <button className="rf-filter-bar-action" onClick={() => setLayoutRetry(n => n + 1)}>
            Try again
          </button>
        </div>
      )}

      {createdHidden && (
        <div className="rf-filter-bar rf-filter-bar--notice" role="status">
          <EyeOff size={15} aria-hidden="true" />
          <span>{createdHidden.message}</span>
          <button
            className="rf-filter-bar-action"
            onClick={() => setTypeVisibility(v => ({ ...v, [createdHidden.kind]: true }))}
          >
            Show {typeLabel(createdHidden.kind).toLowerCase()}
          </button>
          <button
            className="rf-filter-bar-dismiss"
            onClick={() => setCreatedHidden(null)}
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Some types hidden, some showing: the canvas looks plausible, so the only
          clue is here. Named counts, one click to undo. (#754) */}
      {!loading && hidden.filtering && !hidden.allHidden && hidden.hiddenCount > 0 && !restoreBarDismissed && (
        <div className="rf-filter-bar">
          <EyeOff size={15} aria-hidden="true" />
          <span><strong>{hiddenPhrase}</strong> {hidden.hiddenCount === 1 ? 'is' : 'are'} hidden by your view filter.</span>
          <button className="rf-filter-bar-action" onClick={() => setTypeVisibility(ALL_TYPES_VISIBLE)}>
            Show all
          </button>
          <button
            className="rf-filter-bar-dismiss"
            onClick={() => setRestoreBarDismissed(true)}
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      )}

      <div className="rf-content-row">
        <div
          className="rf-canvas"
          onContextMenu={e => e.preventDefault()}
        >
          {/* Everything the filter left out. Without this the canvas is indistinguishable
              from a broken page — which is exactly how it read for a whole evening. */}
          {!loading && hidden.allHidden && (
            <div className="rf-empty">
              <div className="rf-empty-icon" aria-hidden="true"><EyeOff size={24} /></div>
              <h3 className="rf-empty-title">All entity types are hidden</h3>
              <p>
                Your world has <strong>{hiddenPhrase}</strong>. They are filtered out of
                this view, not missing.
              </p>
              <button className="rf-empty-action" onClick={() => setTypeVisibility(ALL_TYPES_VISIBLE)}>
                Show all entity types
              </button>
              <p className="rf-empty-note">This setting is saved in this browser, so it persists between visits.</p>
            </div>
          )}
          {!loading && !hidden.allHidden && nodes.length === 0 && (
            <div className="rf-empty">
              {scopeParam === 'book'
                ? (includedSeriesIds.length > 0
                  ? <p>No entities are associated with <strong>"{scopeName}"</strong> or its series.</p>
                  : <p>No entities are directly associated with <strong>"{scopeName}"</strong>.</p>)
                : scopeParam === 'series'
                ? <p>No entities are associated with <strong>"{scopeName}"</strong> yet.</p>
                : <><h3 className="rf-empty-title">No World Elements Found</h3><p>{isReadonly ? 'Start creating your world on TryChaptery.com!' : 'Start creating your world by adding characters, locations, and events.'}</p></>
              }
            </div>
          )}
          <div
            className="rf-canvas-inner"
            style={{ visibility: loading || nodes.length === 0 || hidden.allHidden ? 'hidden' : 'visible' }}
          >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onInit={instance => { rfInstance.current = instance; }}
            onEdgesChange={onEdgesChange}
            onConnect={isReadonly ? undefined : handleConnect}
            onEdgesDelete={isReadonly ? undefined : handleEdgesDelete}
            onNodesDelete={isReadonly ? undefined : handleNodesDelete}
            onBeforeDelete={isReadonly ? undefined : handleBeforeDelete}
            onReconnect={isReadonly ? undefined : handleReconnect}
            onEdgeClick={(_e, edge) => openEdgePanel(edge.id)}
            onEdgeDoubleClick={(_e, edge) => openEdgePanel(edge.id)}
            onNodeClick={handleNodeClick}
            onNodeContextMenu={isReadonly ? undefined : handleNodeContextMenu}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode={ConnectionMode.Loose}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesDraggable={!isReadonly}
            nodesConnectable={!isReadonly}
            elementsSelectable
            deleteKeyCode={isReadonly ? null : 'Delete'}
            reconnectRadius={isReadonly ? 0 : 20}
            minZoom={0.2}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            {embedTheme !== 'dark' && <Background color="#e5e7eb" gap={20} />}
            <Controls />
            <MiniMap
              nodeColor={n => n.type === 'character' ? '#196773' : n.type === 'location' ? '#5CA383' : '#c97a1e'}
              maskColor="rgba(245,245,247,0.8)"
            />
            <Panel position="bottom-right">
              <span className="rf-save-hint">
                Drag handles to connect · Click edge to edit · Right-click node for all relationships · Auto-saves
              </span>
            </Panel>
          </ReactFlow>
          </div>
        </div>

        {/* Right-click context menu for selecting / deleting overlapping edges */}
        {nodeContextMenu && (() => {
          const menuEdges = nodeContextMenu.edgeIds.map(id => edges.find(e => e.id === id)).filter(Boolean) as Edge[];
          const nodeName = nodes.find(n => n.id === nodeContextMenu.nodeId)?.data.label as string ?? '';
          return (
            <>
              <div className="rf-ctx-backdrop" onClick={() => setNodeContextMenu(null)} />
              <div
                className="rf-ctx-menu"
                style={{ left: nodeContextMenu.x, top: nodeContextMenu.y }}
              >
                <div className="rf-ctx-header">{nodeName}</div>
                {menuEdges.map(edge => {
                  const isSource = edge.source === nodeContextMenu.nodeId;
                  const otherId = isSource ? edge.target : edge.source;
                  const otherName = nodes.find(n => n.id === otherId)?.data.label as string ?? '?';
                  const relType = edge.data?.label as string ?? 'related';
                  const color = REL_TYPE_COLORS[relType] ?? '#196773';
                  return (
                    <div key={edge.id} className="rf-ctx-row">
                      <button
                        className="rf-ctx-rel-btn"
                        onClick={() => {
                          setEdges(eds => eds.map(e => ({ ...e, selected: e.id === edge.id })));
                          setNodeContextMenu(null);
                        }}
                      >
                        <span className="rf-ctx-type" style={{ color }}>{relType}</span>
                        <span className="rf-ctx-arrow">{isSource ? '→' : '←'}</span>
                        <span className="rf-ctx-name">{otherName}</span>
                      </button>
                      <button
                        className="rf-ctx-delete-btn"
                        title="Delete relationship"
                        onClick={() => { deleteRelationship(edge.id); setNodeContextMenu(null); }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}

        {roEntityInfo && (
          <ReadOnlyInfoPanel
            info={roEntityInfo}
            onClose={() => { setSelectedPanelEntity(null); setFocusedNodeId(null); }}
          />
        )}

        {roRelInfo && (
          <ReadOnlyInfoPanel
            info={roRelInfo}
            onClose={() => setSelectedPanelRelationship(null)}
          />
        )}

        {!isReadonly && selectedPanelEntity && (
          <EntitySidePanel
            entity={selectedPanelEntity}
            characters={charactersRef.current.map(c => ({
              entityId: c.entityId,
              entityType: 'character' as const,
              name: c.name,
              description: c.description ?? '',
              metadata: c.metadata ?? {},
              bookIds: c.bookIds ?? [],
              seriesIds: c.seriesIds ?? [],
            }))}
            locations={locationsRef.current.map(l => ({
              entityId: l.entityId,
              entityType: 'location' as const,
              name: l.name,
              description: l.description ?? '',
              metadata: l.metadata ?? {},
              bookIds: l.bookIds ?? [],
              seriesIds: l.seriesIds ?? [],
            }))}
            books={booksRef.current}
            seriesList={seriesListRef.current}
            onClose={() => setSelectedPanelEntity(null)}
            onSave={handlePanelEntitySave}
            onDelete={handlePanelEntityDelete}
            onAutoRelate={handleAutoRelate}
            onCreateEntity={handlePanelEntityCreate}
            onImageUploaded={handlePanelImageUploaded}
            vocab={vocab}
          />
        )}

        {!isReadonly && selectedPanelRelationship && (
          <RelationshipPanel
            relationship={selectedPanelRelationship.rel}
            sourceEntity={selectedPanelRelationship.source}
            targetEntity={selectedPanelRelationship.target}
            onClose={() => setSelectedPanelRelationship(null)}
            onSave={handlePanelRelSave}
            onDelete={handlePanelRelDelete}
            onSwap={handleSwapDirection}
            vocab={vocab}
          />
        )}

        {drawer && (
          <aside className="rt-drawer">
            <div className="rt-drawer-header">
              <h3>{drawer === 'view' ? 'View settings' : 'World settings'}</h3>
              <button
                className="rt-drawer-close"
                onClick={() => setDrawer(null)}
                aria-label={`Close ${drawer === 'view' ? 'View' : 'World'} settings`}
              >
                <X size={16} />
              </button>
            </div>

            {drawer === 'view' && (
              <div className="rt-drawer-body">
                <div className="rt-drawer-section">
                  <span className="rt-drawer-label">Direction</span>
                  <div className="rt-drawer-row">
                    {(Object.keys(DIRECTION_LABELS) as LayoutSettings['rankdir'][]).map(d => (
                      <button
                        key={d}
                        className={`rt-opt${layoutSettings.rankdir === d ? ' active' : ''}`}
                        onClick={() => setLayoutSettings(s => ({ ...s, rankdir: d }))}
                      >{DIRECTION_LABELS[d]}</button>
                    ))}
                  </div>
                </div>

                <div className="rt-drawer-section">
                  <span className="rt-drawer-label">Spacing</span>
                  <div className="rt-drawer-row">
                    {(Object.keys(SPACING_LABELS) as LayoutSettings['spacing'][]).map(sp => (
                      <button
                        key={sp}
                        className={`rt-opt${layoutSettings.spacing === sp ? ' active' : ''}`}
                        onClick={() => setLayoutSettings(s => ({ ...s, spacing: sp }))}
                      >{SPACING_LABELS[sp]}</button>
                    ))}
                  </div>
                </div>

                <div className="rt-drawer-section">
                  <span className="rt-drawer-label">Line style</span>
                  <div className="rt-drawer-row">
                    {(Object.keys(EDGE_ROUTING_LABELS) as EdgeRouting[]).map(r => (
                      <button
                        key={r}
                        className={`rt-opt${layoutSettings.edgeRouting === r ? ' active' : ''}`}
                        onClick={() => setLayoutSettings(s => ({ ...s, edgeRouting: r }))}
                      >{EDGE_ROUTING_LABELS[r]}</button>
                    ))}
                  </div>
                </div>

                <div className="rt-drawer-section">
                  <button className="rt-drawer-primary" onClick={handleAutoLayout}>
                    Apply Layout
                  </button>
                </div>

                <div className="rt-drawer-divider" />

                <div className="rt-drawer-section">
                  <span className="rt-drawer-label">Show types</span>
                  <div className="rt-drawer-row rt-type-row">
                    {TYPE_META.map(t => (
                      <button
                        key={t.key}
                        className={`rt-opt rt-type-opt${typeVisibility[t.key] ? ' active' : ''}`}
                        onClick={() => setTypeVisibility(v => ({ ...v, [t.key]: !v[t.key] }))}
                        title={`${typeVisibility[t.key] ? 'Hide' : 'Show'} ${t.label.toLowerCase()} and their relationships`}
                      >
                        <span className="rt-type-dot" style={{ background: t.color }} />
                        <span className="rt-type-label">{t.label}</span>
                        {/* The count answers "where did my cast go" without making
                            the writer toggle each one to find out. */}
                        <span className="rt-type-count">
                          {typeVisibility[t.key]
                            ? hidden.counts[t.key]
                            : `${hidden.counts[t.key]} hidden`}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rt-drawer-divider" />

                <div className="rt-drawer-section">
                  <span className="rt-drawer-label">Focus</span>
                  <div className="rt-drawer-row">
                    {focusedNodeId
                      ? <button className="rt-opt" onClick={() => setFocusedNodeId(null)}>Clear focus</button>
                      : <button className="rt-opt" disabled>No node focused</button>
                    }
                  </div>
                </div>
              </div>
            )}

            {drawer === 'world' && (
              <VocabularyDrawerBody
                vocab={vocab}
                onUpdate={handleVocabUpdate}
                activeTab={worldTab}
                setActiveTab={setWorldTab}
              />
            )}
          </aside>
        )}
      </div>

    </div>
  );
};

export default RelationshipPage;
